import { fitWorkoutName } from "./workoutNameCap";
import { wireNumber } from "./wireNumber";
import { formatDistance, type DistanceUnit } from "./distanceCell";
/* The workout builder — the rules, not the screen.
 *
 * `docs/EVERFIT-MAP.md` §8 maps Everfit's builder; `docs/WEB-APP-PLAN.md` §2b is the owner's
 * design rule, and the two pull in opposite directions on purpose. His words, after logging into
 * all three competitors: *"i see they all got a lot extra bs which is too much info. i still want
 * full functionality but i like minimalism, and not feel like lost."* Their row editor is a
 * SEVENTEEN-field tracking vocabulary, five section formats, a "Use %" mode and a "Customize
 * fields" panel. Reproducing that surface reproduces exactly what he is rejecting.
 *
 * So the design question §2b poses is **which two or three fields a coach touches on nine rows
 * out of ten** — and it says that is answerable by measuring the catalogue rather than by
 * opinion. It was measured, live, through RLS as the coach, before any of this was written.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * THE MEASUREMENT THAT DECIDES THE SHAPE OF THE ROW EDITOR (2026-08-28)
 *
 *   699 `coach_workout_sets` rows under 323 `coach_workout_exercises`, in 20 workouts.
 *
 *   **306 of the 323 exercises — 94.7% — have EVERY SET IDENTICAL.**
 *   Only 17 vary reps, 15 vary weight, 10 vary rest, and ZERO vary time.
 *
 * ⚠️ AND IT WAS RE-MEASURED AFTER SPLITTING BY WHO WROTE THE ROWS, BECAUSE THE FIRST READING
 * WAS ABOUT MY OWN SEED SCRIPT. Most of those rows were written by `tools/v6-coach-library`
 * (V6-8) and copied by V7-2's copy-on-assign backfill, not typed by a human in a builder —
 * *"the most convenient verification data was the data we wrote"*, which this repo has paid for
 * before. Split by origin, on TEMPLATES only:
 *
 *   | origin                          | exercises | all sets identical |
 *   |---------------------------------|-----------|--------------------|
 *   | hand-made in the real builder   |        27 |    24  (88.9%)     |
 *   | seed-written (V6-8)             |       101 |   100  (99.0%)     |
 *
 * **The finding survives the split, in both populations independently.** That is what licenses
 * acting on it. What did NOT survive the split is a field-usage ranking — `notes` looked like the
 * second most-used field at 64% and is 95% in the seed against ~12% by hand, so it is a fact
 * about the seed and is deliberately NOT used to rank anything here.
 *
 * TWO MORE, both holding in both populations and both structural rather than stylistic:
 *
 *   - **NO exercise carries both reps and time — 0 of 323.** A movement is counted or it is
 *     timed. So the editor shows ONE measure, never a row of empty boxes for the other.
 *   - **`rounds` and `distance_m` are populated on ZERO of the 699 sets**, and on zero of the
 *     108 library exercises' `tracking_fields`. Not offered.
 *
 * ⇒ **THE DEFAULT IS ONE LINE, AND THE PER-SET TABLE IS THE DISCLOSURE.** `3 × 8 · rest 90s` is
 * what nine rows out of ten need; the table that Everfit shows always is one deliberate step
 * away, and everything it can express is still expressible. That is §2b's three parts at once:
 * full functionality, minimal surface, and nothing whose meaning needs explaining.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ WHICH BOXES APPEAR IS NOT THIS FILE'S DECISION AND MUST NOT BECOME ONE.
 * `coach_exercises.tracking_fields` is a real column, populated on 108 of 108 of his library
 * rows, and the phone already reads it (`WorkoutExerciseDraft.columns`). It holds only four
 * shapes across the whole library — `reps` 37, `time` 36, `weight+reps` 34, `weight+time` 1 —
 * so the answer already exists per exercise and neither surface has to ask the coach twice.
 * Deriving it here from a name or a category would be a second statement of a stored fact, which
 * is this repo's defining bug class. `columnsFor` READS the column; it does not infer it.
 */

/** What the athlete fills in for a set. Mirrors `TrackingField` in `Models/CoachExerciseFields.swift`,
 *  whose raw values are exactly what `coach_exercises.tracking_fields` stores. */
import {
  FORMAT_FIELDS, PER_EXERCISE_KEYS, LEGACY_DURATION_MINUTES, KEY_TOTAL_SECONDS, KEY_ROUND_COUNT,
  KEY_ROUNDS, KEY_WORK_SECONDS, KEY_TIME_CAP_SECONDS, type FormatField,
} from "./formatConfig";

export type TrackingField = "reps" | "weight" | "time" | "rounds" | "distance_m" | "distance_km";

/** `coach_workout_sections.format` — the seven the CHECK constraint allows. */
export type SectionFormat =
  | "straight_sets" | "superset" | "circuit" | "emom" | "amrap" | "timed_interval" | "for_time";

/** `coach_workout_sections.section_type` — the four the CHECK constraint allows. */
export type SectionType = "workout" | "warm_up" | "cool_down" | "recovery";

/** `coach_workout_sets.set_type` — the four the CHECK constraint allows. */
export type SetType = "regular" | "warm_up" | "drop_set" | "failure";

export type SetDraft = {
  type: SetType;
  reps: number | null;
  weightKg: number | null;
  timeSeconds: number | null;
  rounds: number | null;
  restSeconds: number | null;
  /** `coach_workout_sets.distance_m`. Nothing writes it yet (0 of 1,399 live rows) and no live
   *  exercise tracks distance — it is carried so the round trip cannot drop it later. */
  distanceM: number | null;
};

export type ExerciseDraft = {
  /** `coach_exercises.id` — the LIBRARY row, which is the vocabulary both sides share. */
  libraryExerciseId: string;
  name: string;
  /** Straight from `coach_exercises.tracking_fields`. Never inferred here. */
  trackingFields: TrackingField[];
  eachSide: boolean;
  /** The BALANCE clock (his yes 2026-10-07) — `coach_workout_exercises.hold_until_lost`: a hold counted UP until lost, no
   *  target. Optional on the draft (nine test fixtures build one without it); every writer states it (`=== true`). */
  holdUntilLost?: boolean;
  tempo: string | null;
  note: string | null;
  supersetWithNext: boolean;
  sets: SetDraft[];
};

/** `coach_workout_sections.format_config`, CANONICAL keys only — see `readFormatConfig`. */
export type FormatConfig = Record<string, number>;

export type SectionDraft = {
  name: string | null;
  format: SectionFormat;
  sectionType: SectionType;
  description: string | null;
  /** The format's own numbers — an AMRAP's cap, a circuit's rounds and rest. Empty for straight
   *  sets. Written as `format_config`; the phone's player reads exactly these keys. */
  formatConfig: FormatConfig;
  /* 🔴 `coach_workout_sections.is_implicit` (§6.5, LIVE 2026-09-19). TRUE when the app made this
     section to hold an exercise a coach dragged outside every section, rather than a coach naming
     one. An implicit section draws no bar and no name, takes straight sets and the `workout`
     block, and is deleted when its last exercise leaves. ⚠️ CUSTOMISING ONE NAMES IT, which makes
     it a real section — so this only ever goes TRUE -> FALSE, and never the other way by accident.
     ⚠️ NOT OPTIONAL: the column is NOT NULL with a default, and a draft field that could be
     `undefined` would let a caller omit it and write a section whose kind nobody decided. */
  isImplicit: boolean;
  exercises: ExerciseDraft[];
};

export type WorkoutDraft = {
  /** Absent for a workout that has never been saved. */
  id: string | null;
  name: string;
  description: string | null;
  sections: SectionDraft[];
};

/** A blank set. Rest is the only field with a default, and the measurement is why: it is present
 *  on 601 of 699 sets (86%) — the most-filled field in the whole vocabulary. */
export function emptySet(): SetDraft {
  return { type: "regular", reps: null, weightKg: null, timeSeconds: null, rounds: null, restSeconds: null, distanceM: null };
}

/**
 * The offered section formats, and `superset` is absent ON PURPOSE.
 *
 * The database CHECK allows seven; a coach is offered six. `CoachWorkoutDraft.swift` states the
 * reason and it is the right one: *"a superset is made by joining exercises together in the
 * builder, not by declaring one in advance and then filling it."* A coach who picks "superset"
 * from a menu has an empty container and no way to know what goes in it.
 */
export const OFFERED_FORMATS: SectionFormat[] = [
  "straight_sets", "circuit", "emom", "amrap", "timed_interval", "for_time",
];

/**
 * What each format DOES, in the coach's terms.
 *
 * §8d's verdict is **"KEEP AND STEAL THE CAPTIONS, NOT THE LIST"** — five formats each explained
 * in one line at the moment of choosing is worth more than six named correctly and left bare.
 * This is also §2b's *no control whose meaning needs explaining*: the explanation is present at
 * the point of choice, so it never becomes a tooltip.
 */
export const FORMAT_CAPTION: Record<SectionFormat, string> = {
  straight_sets: "Exercise by exercise, resting between sets. Most strength work.",
  circuit:       "Round after round through every exercise, resting at the end of a round.",
  emom:          "A fresh minute starts each exercise, and the rest is whatever is left.",
  amrap:         "As many rounds as possible inside a time you set.",
  timed_interval:"The app runs the clock — a work time and a rest time per exercise.",
  for_time:      "Finish the prescribed work as fast as possible.",
  superset:      "Two or more exercises alternated with no rest between them.",
};

/* ── The format's own numbers ─────────────────────────────────────────────────────────────────
 *
 * ⚠️ MEASURED 2026-09-05: this builder wrote NO `format_config` at all, so a coach's AMRAP left the
 * web with no cap and the phone counted UP for ever; the iPhone builder wrote `duration_minutes`
 * and the engine `total_seconds` — three spellings of one fact, the repo's defining bug class.
 * The fields a format asks for are authored ONCE in Swift (`SectionFormatConfig.fields(for:)`) and
 * GENERATED into `formatConfig.ts`; the reading rule below mirrors the Swift `read` — canonical
 * keys kept, the legacy key resolved by format, never written — and is pinned by its tests.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/** What a section of this format asks the coach for, in the builder's order. */
/**
 * A format's NAME, as the section bar prints it.
 *
 * 🔴 IT LIVED IN `WorkoutBuilder.tsx` AND NOW LIVES HERE, because W17d gave it a SECOND surface.
 * The overview window prints a section's format beside its name, and a second copy of this table
 * is the third-surface trap this repo has already paid for twice in this very module — the
 * preview's `UNNAMED_SECTION` against the shared unnamed-section rule (now `sectionName`, W104), and `collapsedLabel` against the two
 * grammars that existed before it. Two spellings that agree today are one edit from disagreeing.
 *
 * ⚠️ `Record<SectionFormat, string>` rather than a function with a default: a new format then
 * fails to COMPILE until it is named, instead of silently printing its own raw value.
 */
export const FORMAT_LABEL: Record<SectionFormat, string> = {
  straight_sets: "Straight sets", circuit: "Circuit", emom: "EMOM",
  amrap: "AMRAP", timed_interval: "Interval", for_time: "For time", superset: "Superset",
};

export function formatFields(format: SectionFormat): readonly FormatField[] {
  return FORMAT_FIELDS[format] ?? [];
}

/** Every spelling a stored row may carry, resolved to the canonical keys for its format. */
export function readFormatConfig(format: SectionFormat, raw: unknown): FormatConfig {
  const out: FormatConfig = {};
  if (raw === null || typeof raw !== "object") return out;
  const dict = raw as Record<string, unknown>;
  const owned = new Set<string>([
    ...formatFields(format).map((f) => f.key),
    ...(PER_EXERCISE_KEYS[format] ?? []),
  ]);
  for (const [key, value] of Object.entries(dict)) {
    if (!owned.has(key)) continue;
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(n) && n > 0) out[key] = Math.trunc(n);
  }
  // `duration_minutes` meant two facts under one key, told apart only by the format: minutes on
  // an AMRAP's clock, and how many one-minute intervals an EMOM runs. A canonical key present wins.
  const legacy = dict[LEGACY_DURATION_MINUTES];
  const minutes = typeof legacy === "number" ? legacy : Number(legacy);
  if (Number.isFinite(minutes) && minutes > 0) {
    if (format === "amrap" && out[KEY_TOTAL_SECONDS] === undefined) out[KEY_TOTAL_SECONDS] = minutes * 60;
    if (format === "emom" && out[KEY_ROUND_COUNT] === undefined) out[KEY_ROUND_COUNT] = minutes;
  }
  return out;
}

/** A fresh section of this format: every field with a default at its default. */
export function defaultFormatConfig(format: SectionFormat): FormatConfig {
  const out: FormatConfig = {};
  for (const f of formatFields(format)) if (f.defaultValue !== null) out[f.key] = f.defaultValue;
  return out;
}

/** Re-home a config onto another format: the keys both formats share survive, the rest is
 *  dropped and the new format's defaults fill in. "Three rounds" of a circuit turning into an
 *  AMRAP must not silently become three minutes. */
export function rehomeFormatConfig(config: FormatConfig, from: SectionFormat, to: SectionFormat): FormatConfig {
  const next = defaultFormatConfig(to);
  const toKeys = new Set(formatFields(to).map((f) => f.key));
  for (const f of formatFields(from)) {
    if (toKeys.has(f.key) && config[f.key] !== undefined) next[f.key] = config[f.key] as number;
  }
  return next;
}

/** The required fields a section of this format is still missing — what the coach is told. */
export function formatConfigProblems(format: SectionFormat, config: FormatConfig | undefined): FormatField[] {
  // A section shaped before this field existed carries none — the same as nothing set.
  const cfg = config ?? {};
  return formatFields(format).filter((f) => f.isRequired && cfg[f.key] === undefined);
}

/** The one figure that names a timed or rounded section, for a list: "10 min", "3 rounds". */
export function formatHeadline(format: SectionFormat, config: FormatConfig): string | null {
  const minutes = (s: number) => (s % 60 === 0 ? `${s / 60} min` : clock(s));
  switch (format) {
    case "amrap": {
      const cap = config[KEY_TOTAL_SECONDS];
      return cap === undefined ? null : minutes(cap);
    }
    case "emom": {
      const n = config[KEY_ROUND_COUNT];
      if (n === undefined) return null;
      const every = config[KEY_WORK_SECONDS] ?? 60;
      return every === 60 ? `${n} min` : `${n} × ${clock(every)}`;
    }
    case "circuit":
    case "timed_interval": {
      const r = config[KEY_ROUNDS];
      return r === undefined ? null : `${r} ${r === 1 ? "round" : "rounds"}`;
    }
    case "for_time": {
      const r = config[KEY_ROUNDS];
      if (r === undefined) return null;
      const base = `${r} ${r === 1 ? "set" : "sets"}`;
      const cap = config[KEY_TIME_CAP_SECONDS];
      return cap === undefined ? base : `${base} · cap ${minutes(cap)}`;
    }
    default:
      return null;
  }
}

/**
 * Whether exercises inside a section of this format may be joined into a superset.
 *
 * REASONED FROM WHAT A SUPERSET IS, and carried over from `CoachWorkoutDraft.swift` rather than
 * re-decided, because two surfaces disagreeing about which formats can hold a pair is exactly
 * the class of bug the compatibility rule exists to stop. A superset says *"no rest between
 * these two, then rest"* — so it is only meaningful where the COACH owns the rest. EMOM is
 * excluded because a fresh minute decides when each exercise starts; `for_time` because the
 * whole block is one clock and a couplet inside it has nothing to mean.
 */
export function allowsSupersets(format: SectionFormat): boolean {
  return format === "straight_sets" || format === "circuit" || format === "amrap"
      || format === "timed_interval" || format === "superset";
}

/**
 * Which measure columns an exercise shows inside a section of the given format.
 *
 * ⚠️ THE STORED FIELDS DECIDE, AND AN EMPTY LIST FALLS BACK TO REPS — the phone's own rule
 * (`exercise.trackingFields.isEmpty ? [.reps] : exercise.trackingFields`), reproduced so the two
 * surfaces cannot disagree about a movement with no declared fields. All 108 of his library rows
 * declare some, so the fallback is unexercised by real data today and is kept because the column
 * is nullable and a coach can create a row.
 *
 * ⚠️ AN INTERVAL APPENDS TIME RATHER THAN SUBSTITUTING IT. A coach putting push-ups into a Tabata
 * is prescribing "as many as you can in twenty seconds", so the rep column has to survive; the
 * clock is added for THIS SECTION only and the library row is untouched.
 *
 * `distance_m` / `distance_km` are carried in the type and never offered — `TrackingField` on the
 * phone marks them *"defined, deliberately not offered yet — owner's call"*, and zero rows use
 * them. Writing the vocabulary down once is what stops the next component inventing a spelling.
 */
export function columnsFor(trackingFields: TrackingField[], format: SectionFormat): TrackingField[] {
  const base = trackingFields.length === 0 ? (["reps"] as TrackingField[]) : [...trackingFields];
  if (format === "timed_interval" && !base.includes("time")) return [...base, "time"];
  return base;
}

/**
 * Are all of these sets the same set?
 *
 * 🔴 THIS IS THE RULE THE WHOLE EDITOR HANGS ON — 94.7% of real exercises answer yes, so it
 * decides whether a coach sees one line or a table. Every field is compared, including `type`
 * and `restSeconds`: a warm-up set among working sets is a real difference, and rest is the most
 * populated field in the vocabulary, so ignoring either would collapse a row that genuinely
 * varies into a single line and silently discard the difference on the next edit.
 *
 * ⚠️ AN EMPTY LIST IS NOT UNIFORM. There is no set to show on one line, and an exercise with no
 * sets is a validation problem rather than a display state — answering `true` here would let a
 * collapsed row render a summary of nothing.
 */
export function isUniform(sets: SetDraft[]): boolean {
  if (sets.length === 0) return false;
  const [first, ...rest] = sets;
  if (first === undefined) return false;
  return rest.every((s) =>
    s.type === first.type && s.reps === first.reps && s.weightKg === first.weightKg
    && s.timeSeconds === first.timeSeconds && s.rounds === first.rounds
    && s.restSeconds === first.restSeconds && s.distanceM === first.distanceM);  // W136 B2: 400 m and 800 m are not one set
}

/**
 * Expand a single set into `count` copies — what the one-line editor writes.
 *
 * Copies rather than references, so editing one set later cannot change its siblings.
 */
export function expandUniform(template: SetDraft, count: number): SetDraft[] {
  const n = Math.max(1, Math.floor(count));
  return Array.from({ length: n }, () => ({ ...template }));
}

/**
 * W17c — WHAT A NON-UNIFORM EXERCISE SAYS.
 *
 * 🔴 IT USED TO SAY `3 sets, varying` AND NOTHING ELSE, which told a coach the one thing they
 * already knew. MEASURED on the live fixture: his `Bodyweight Squat` in `Main Strength` is 30, 30
 * and 32.5 kg for 10, 10 and 8 reps, and its label mentioned **no load at all** — so the screen
 * was silent about a loaded lift for the second time in one night, by a different mechanism than
 * W17b's.
 *
 * ⚠️ THE RANGE, NEVER A MAXIMUM — THIS REPO HAS ALREADY PAID FOR THE OTHER ANSWER. The codex:
 * *"a summary built from the day's MAXIMA describes a set nobody did — topWeight from one set and
 * bestReps from another read `2 × 8 @ 60 kg` for a 60×5 + 50×8 day."* A range is the only summary
 * of a varying group that cannot invent a set: every number in it was prescribed, and the pair
 * states the spread rather than pretending to a single value.
 *
 * ⚠️ A COLUMN THE SETS AGREE ON PRINTS ONCE. `30–30 kg` is a range with no range in it, and it
 * reads as a defect. The min and the max are compared, not the count.
 *
 * ⚠️ THE MEASURE ORDER IS THE UNIFORM BRANCH'S, deliberately, even though the brief that asked
 * for this wrote its example weight-first: the two branches of ONE function reading in two orders
 * is the same disagreement this file keeps being bitten by, one level smaller. Reps/time/rounds,
 * then the load, then the rest.
 *
 * ⚠️ A WARM-UP IS IN THE RANGE. It is a set the athlete performs, and a ramp's whole shape is
 * the climb — `3 sets · 6–10 reps · @ 40–70 kg` IS the ramp. Excluding it would describe a
 * lighter session than the one prescribed.
 *
 * ⚠️ AND NO SET TYPE IS NAMED. The uniform branch appends `to failure` or `drop set` because
 * there is one type to name; here there are several by definition, and naming one would be a
 * claim about the others.
 */
/** A column's range over the sets — `lo–hi`, or the one value when they agree, or null when no set carries it. */
function span(values: (number | null)[], render: (n: number) => string): string | null {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return null;
  const lo = Math.min(...present);
  const hi = Math.max(...present);
  return lo === hi ? render(lo) : `${render(lo)}–${render(hi)}`;
}

function varyingParts(sets: SetDraft[], unit: DistanceUnit): string[] {
  const parts: string[] = [plural(sets.length, "set")];
  const distance = span(sets.map((x) => x.distanceM), (n) => formatDistance(n, unit));
  if (distance !== null) parts.push(distance);
  // ⚠️ REPS AND ROUNDS PLURALISE ON THE HIGH END, which is what an English range does: `8–10
  //    reps`, and `1–1 rep` can never occur because equal ends collapse to a single value.
  const reps = span(sets.map((x) => x.reps), (n) => String(n));
  if (reps !== null) parts.push(`${reps} ${Math.max(...sets.map((x) => x.reps ?? 0)) === 1 ? "rep" : "reps"}`);
  const time = span(sets.map((x) => x.timeSeconds), clock);
  if (time !== null) parts.push(time);
  const rounds = span(sets.map((x) => x.rounds), (n) => String(n));
  if (rounds !== null) parts.push(`${rounds} ${Math.max(...sets.map((x) => x.rounds ?? 0)) === 1 ? "round" : "rounds"}`);
  const load = span(sets.map((x) => x.weightKg), (n) => String(n));
  if (load !== null) parts.push(`@ ${load} kg`);
  const rest = restSpan(sets);
  if (rest !== null) parts.push(`rest ${rest}`);
  return parts;
}

/**
 * 🔴 W137 — THE REST, ALONE, AS THE BUILDER'S SENTENCE SAYS IT: `1:30`, `45s`, `60s–2:00` across varying sets, or null when
 * no set carries one. It is the SAME `span` + `clock` `collapsedLabel` prints after `rest` — both branches of that function
 * ask THIS, so the Week's extended column (`Rest 1:30`, his 2026-10-02 word: *"plus any comments though (coach notes) and
 * rest times"*) can never say a different rest from the preview one click away.
 */
export function restSpan(sets: SetDraft[]): string | null {
  return span(sets.map((x) => x.restSeconds), clock);
}

/**
 * 🔴 THE SHORT FORM — the day card's dose (Fable fix (a) on W107, 2026-09-30). The full sentence wrapped to three lines on
 * a Week card (`2 × 15 reps · @ 12.5 kg · rest 1:30`); a coach scanning a week reads the count, the reps or the time, and
 * the load. So: `2 × 15 @ 12.5 kg`, `3 × 30s`, `4 × 8` — no `reps` word (`N × M` IS reps), no rest, no set type.
 * ⚠️ UNIFORMITY HERE ASKS ONLY THE COLUMNS IT PRINTS: two sets differing only in rest are `2 × 8`, never a range.
 * ⚠️ VARIATION IS THE SAME RANGE `varyingParts` DRAWS (never a maximum), so `3 × 6–10 @ 40–70 kg` invents no set.
 * ⚠️ AT MOST TWO PARTS — the count-and-measure, then the load — so a narrow card breaks between them, never inside one.
 */
function shortParts(sets: SetDraft[], unit: DistanceUnit): string[] {
  if (sets.length === 0) return [];
  const measures: string[] = [];
  const distance = span(sets.map((x) => x.distanceM), (n) => formatDistance(n, unit));
  if (distance !== null) measures.push(distance);
  const reps = span(sets.map((x) => x.reps), (n) => String(n));
  if (reps !== null) measures.push(reps);
  const time = span(sets.map((x) => x.timeSeconds), clock);
  if (time !== null) measures.push(time);
  const rounds = span(sets.map((x) => x.rounds), (n) => String(n));
  if (rounds !== null) measures.push(`${rounds} ${Math.max(...sets.map((x) => x.rounds ?? 0)) === 1 ? "round" : "rounds"}`);
  const parts = [measures.length === 0 ? plural(sets.length, "set") : `${String(sets.length)} × ${measures.join(" ")}`];
  const load = span(sets.map((x) => x.weightKg), (n) => String(n));
  if (load !== null) parts.push(`@ ${load} kg`);
  return parts;
}

/** `1 rep` / `8 reps`, `1 set` / `3 sets`. The `${n === 1 ? "" : "s"}` idiom is written out in
 *  eight other files; this is not a sweep of them, it is the one place W17b needed. */
function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * THE ONE-LINE LABEL FOR A UNIFORM EXERCISE — the PREVIEW's sentence.
 *
 * 🔴 ITS CONSUMER IS `workoutPreview`, AND THE BUILDER IS NOT ONE. This docstring used to open
 * *"an EDITOR affordance… what am I editing"*, and that stopped being true at §6 adoption 2,
 * which deleted the builder's collapsed "Sets / Reps / Rest + Vary by set" line along with
 * `expanded`, `setUniform` and `setCount`. The builder draws ONE ROW PER SET, because that is
 * what `coach_workout_sets` holds. MEASURED 2026-09-19: `collapsedLabel(` appears at exactly
 * three call sites in `src` — `workoutPreview.ts` and two test files. A function whose only
 * caller is a test is dead code with a runner in front of it; this one was rescued by W17
 * rather than deleted, and the docstring now says who reads it.
 *
 * ⚠️ IT IS STILL NOT THE ATHLETE'S PRESCRIPTION SENTENCE. The phone renders what the athlete
 * reads through `AssistLoad.prescriptionLine`, which knows about assistance and per-set values
 * and says `3/3/3/3/4 reps`. Two audiences, two sentences; unifying them would put one function
 * in front of both, which is how a rule gets hedged into uselessness.
 *
 * 🔴 W17b — IT PRINTS WHAT THE SET CARRIES, NOT WHAT THE LIBRARY ROW DECLARES. It used to gate
 * every measure on the exercise's `tracking_fields`, and MEASURED over the live fixture that hid
 * **18 of 1,399 set rows' values**. They are not stale data, which is the thing that had to be
 * checked before changing this — they were read one by one:
 *
 *   * `Standing Calf Raise` in his own `Legs & Core`, ELEVEN rows at **15 kg**, on a library row
 *     declaring `["reps"]` — so the line said `4 × 12 reps` about a loaded lift.
 *   * `Bodyweight Squat` in `Main Strength` at **30, 30 and 32.5 kg**, same narrow declaration.
 *   * `Bodyweight Squat` in a Cool Down carrying **60 seconds**, and `Bulgarian Split Squat` in
 *     Recovery carrying **120** — holds, rendering as `1 set · rest 15s` with no hold in them.
 *
 * The library row's vocabulary being too narrow is a fact about the LIBRARY ROW. A value in a
 * set row is something somebody prescribed, and a reader that drops it is choosing the tidier
 * answer over the true one — this repo's `weight_kg is NULL` lesson, one level up: an absent
 * value is not a value, and a PRESENT one is not noise.
 *
 * 🔴 W136 B2 (2026-10-05) — `distanceM` JOINS, WHERE THIS COMMENT SAID IT WOULD. It was left out while distance was
 * *"defined, deliberately not offered yet"*; Fable's design (docs/W136-DISTANCE-ROUNDS.md) offers it, the web builder's
 * cell now writes it, so a line that dropped it would be the W17b mistake again (a present value is not noise).
 * It prints in the coach's UNIT — `unit`, read off the exercise's fields by the caller — never chosen by magnitude (D6).
 * ⚠️ The preview's door carries no fields (`coach_exercises(name)` only), so the preview passes nothing and prints METRES,
 * the stored unit — true, never invented; `5 km` there waits on a door that carries the unit (S3, the readouts).
 *
 * ⚠️ IT OMITS WHAT IT DOES NOT KNOW RATHER THAN PRINTING A ZERO. An absent weight is a weight
 * the coach has not chosen; `@ 0 kg` is a prescription nobody wrote.
 */
export function collapsedLabel(sets: SetDraft[], form: DoseForm = "full", unit: DistanceUnit = "m"): string {
  return collapsedLabelParts(sets, form, unit).join(form === "short" ? " " : " · ");
}

/** `full` — the builder's sentence (the preview, the review sheet); `short` — the Week card's `2 × 15 @ 12.5 kg`. */
export type DoseForm = "full" | "short";

/** `collapsedLabel`'s parts before the join — what a narrow surface breaks BETWEEN and never inside. */
export function collapsedLabelParts(sets: SetDraft[], form: DoseForm = "full", unit: DistanceUnit = "m"): string[] {
  if (form === "short") return shortParts(sets, unit);
  if (!isUniform(sets)) return varyingParts(sets, unit);
  const s = sets[0];
  if (s === undefined) return [];
  const parts: string[] = [];
  const measures: string[] = [];
  if (s.reps !== null) measures.push(plural(s.reps, "rep"));
  // W136 B2 — a run reads `3 × 400 m`, and with its time `3 × 400 m in 1:45` (design §5)
  if (s.distanceM !== null) measures.push(formatDistance(s.distanceM, unit) + (s.timeSeconds !== null ? ` in ${clock(s.timeSeconds)}` : ""));
  else if (s.timeSeconds !== null) measures.push(clock(s.timeSeconds));
  if (s.rounds !== null) measures.push(plural(s.rounds, "round"));
  parts.push(measures.length === 0
    ? plural(sets.length, "set")
    : `${sets.length} × ${measures.join(" ")}`);
  if (s.weightKg !== null) parts.push(`@ ${s.weightKg} kg`);
  const rest = restSpan(sets);
  if (rest !== null) parts.push(`rest ${rest}`);
  if (s.type !== "regular") parts.push(SET_TYPE_LABEL[s.type].toLowerCase());
  return parts;
}

/** Warm-ups before the work, never after one (his 2026-09-11 word). The one sentence both builders say. */
export const SET_ORDER_RULE = "Warm-up sets go before the working sets, never after one.";

/** A warm-up is not work; everything else is (a drop set and a failure set are working sets with a letter). */
/* ── THE SET ROUND TRIP ────────────────────────────────────────────────────────────────────
 *
 * 🔴 §6 RULES THAT THE SET TABLE'S ROUND TRIP IS PROVEN AGAINST `coach_workout_sets` ROWS, FIELD
 * BY FIELD — never against the assembled tree. These two functions ARE that round trip, and they
 * were inline in `WorkoutBuilder.tsx` (one in the read effect, one in the save) where nothing
 * could execute them. Extracted unchanged so `builder.test.ts` can drive rows → drafts → rows and
 * diff them; the component now calls these, so the thing under test is the thing that ships.
 *
 * ⚠️ THE FIELD LIST IS THE SCHEMA'S, NOT A REMEMBERED ONE. MEASURED against the live fixture:
 * `coach_workout_sets` carries `position, set_type, reps, weight_kg, time_seconds, rounds,
 * rest_seconds, distance_m` — there is NO `set_number` and NO `seconds`, and `each_side`/`tempo`
 * live on `coach_workout_exercises`, not here. A test written from the wrong list would compare
 * fields that do not exist and pass vacuously, which is the silence-is-not-success shape.
 *
 * ⚠️ `distance_m` IS CARRIED THROUGH THIS PAIR EVEN THOUGH NOTHING WRITES IT YET — 0 of 1,399
 * live set rows populate it. It is here so that the day a distance column is added to the table,
 * the value cannot be silently dropped on save; a round trip that quietly loses a column is the
 * `withSets` shape §6 names as the warning. */
export type SetRow = {
  position: number;
  set_type: string;
  reps: number | null;
  weight_kg: number | null;
  time_seconds: number | null;
  rounds: number | null;
  rest_seconds: number | null;
  distance_m: number | null;
};

/** Rows as the database holds them → the drafts the table edits. Ordered by `position`. */
export function setsFromRows(rows: readonly SetRow[]): SetDraft[] {
  return [...rows]
    .sort((a, b) => a.position - b.position)
    .map((st) => ({
      type: st.set_type as SetType,
      /* ⚠️ `coach_workout_sets.weight_kg` AND `distance_m` ARE `numeric` — they arrive as the
         STRINGS "62.50" and "1000.00". Coerced here, at the one place the wire row becomes the
         builder's own draft. Caught by the sweep ruler, not by a coach: the set table would have
         shown "62.50" and any arithmetic on it read as text. */
      reps: st.reps, weightKg: wireNumber(st.weight_kg), timeSeconds: st.time_seconds,
      rounds: st.rounds, restSeconds: st.rest_seconds, distanceM: wireNumber(st.distance_m),
    }));
}

/** The drafts → the rows to insert. `position` is the INDEX, which is what makes the trip stable. */
export function rowsFromSets(sets: readonly SetDraft[]): SetRow[] {
  return sets.map((s, i) => ({
    position: i, set_type: s.type,
    reps: s.reps, weight_kg: s.weightKg, time_seconds: s.timeSeconds,
    rounds: s.rounds, rest_seconds: s.restSeconds, distance_m: s.distanceM,
  }));
}

export function isWork(type: SetType): boolean { return type !== "warm_up"; }

/**
 * The set column's text: a typed set its letter, a regular set its WORKING rank — the warm-ups before it
 * do not count (his 2026-09-11 word: "don't count warm up sets as sets, at least visually"), so a ramp
 * reads W · 1 · 2 · 3. The iOS `SetType.label(at:in:)` is the same rule; both are pinned.
 */
export function setLabel(sets: SetDraft[], index: number): string {
  // The COACH's column names every set TYPE by its letter, F included — the phone builder's own rule (it draws
  // `type.letter` and asks `SetType.label` only for a regular set). The athlete's rule is `setLabelOf`.
  if (sets[index]?.type === "failure") return "F";
  return setLabelOf(sets.map((x) => x.type), index);
}

/** `SetType.label(at:in:)` — the ATHLETE's set column over the TYPES alone: W, D, or the working rank. A set to failure is
 *  NUMBERED (his Mon 5 Oct note: *"Shouldn't instead of 6 reps at set 2 be the F? And set 2 say actually 2"*) — its F is the
 *  reps' floor `6+`. What the printed page reads (QUEUE N2b-2). An unknown or null type reads as regular, as iOS does. */
export function setLabelOf(types: readonly (string | null)[], index: number): string {
  if (index < 0 || index >= types.length) return String(index + 1);
  const t = types[index];
  if (t === "warm_up") return "W";
  if (t === "drop_set") return "D";
  return String(types.slice(0, index + 1).filter((x) => x !== "warm_up").length);
}

/** The index of the first warm-up that follows a working set, or null. */
export function rungAfterWorkFault(sets: SetDraft[]): number | null {
  let seenWork = false;
  for (let i = 0; i < sets.length; i++) {
    const s = sets[i];
    if (s === undefined) continue;
    if (isWork(s.type)) seenWork = true;
    else if (seenWork) return i;
  }
  return null;
}

/** What the type select may offer for set `index`: a warm-up only before the work, a working type only with no warm-up after it. */
export function allowedSetTypes(sets: SetDraft[], index: number): SetType[] {
  const workBefore = sets.slice(0, index).some((s) => isWork(s.type));
  const rungAfter = sets.slice(index + 1).some((s) => s.type === "warm_up");
  return (["regular", "warm_up", "drop_set", "failure"] as SetType[]).filter((t) => (t === "warm_up" ? !workBefore : !rungAfter));
}

export const SET_TYPE_LABEL: Record<SetType, string> = {
  regular: "Working", warm_up: "Warm-up", drop_set: "Drop set", failure: "To failure",
};

/**
 * The LETTER a set type carries in a MENU, where there is no position to count from.
 *
 * ⚠️ IT IS NOT `setLabel`, AND THE DIFFERENCE IS THE WHOLE REASON IT EXISTS. `setLabel` answers
 * *"what does THIS ROW print"*, which for a working set is its ORDINAL among the working sets —
 * a menu offering "become a working set" has no row to count. `R` is what their menu prints
 * (`docs/EVERFIT-MAP.md` §8-ter, read live 2026-09-19) and it is what a coach reading the menu
 * beside a table of `W · 1 · 2 · F` needs, because `1` there would name one specific row.
 */
export const SET_TYPE_LETTER: Record<SetType, string> = {
  regular: "R", warm_up: "W", drop_set: "D", failure: "F",
};

/** `90` → `1:30`, `45` → `45s`. Minutes only once there are minutes, because "0:45" reads as a
 *  stopwatch where a coach means three quarters of a minute. */
export function clock(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  /* W136 B2 S2b — a set's time over an hour prints `h:mm:ss` (his word 10-05); under an hour, byte for byte as before. */
  if (seconds >= 3600) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${Math.floor(seconds / 3600)}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}:00` : `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Accepts `90`, `1:30` and `1.30` — the phone's `WorkoutSetDraft.seconds(from:)`, reproduced so a
 * coach typing a rest time on either surface does not have to remember which one this field
 * wanted. Returns null for anything it cannot read, so a typo does not silently become a number.
 */
export function parseSeconds(text: string): number | null {
  const cleaned = text.trim().replace(".", ":");
  if (cleaned === "") return null;
  if (cleaned.includes(":")) {
    const [mm, ss = "0"] = cleaned.split(":", 2);
    const m = Number(mm), s = Number(ss);
    if (!Number.isFinite(m) || !Number.isFinite(s)) return null;
    return Math.round(m) * 60 + Math.round(s);
  }
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n) : null;
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════
 * SUPERSETS — AND THIS IS THE ONE PLACE THE WEB DELIBERATELY DOES NOT DO WHAT THE PHONE DOES.
 *
 * `WorkoutGroupDraft.levelSupersetSets()` levels a joined run UP to its LONGEST member, giving
 * the shorter exercise copies of its own last set. The owner's rule is the opposite, in his own
 * words (`docs/V6-SPEC-V2-DELTA.md` §4): *"the one with the less will win… and there will be
 * added 1 more set alone after the superset."*
 *
 * V6-8 hit this and recorded the consequence: seeding a 3-vs-4 pair would have had the phone's
 * builder *"silently invent a fourth set of the wrong exercise the first time he opened it"*, so
 * every pair in the coach library was authored EQUAL with the surplus as a trailing solo row.
 *
 * ⚠️ AND THAT IS WHY THIS IS COMPATIBLE RATHER THAN A FORK. Equal pairs are the one shape
 * `levelSupersetSets` is INERT on — V6-8's own words. So the web writing his rule produces
 * exactly the input on which the phone's rule does nothing, and the two surfaces cannot disagree
 * about a workout that exists. Writing an UNEQUAL pair is what would fork them.
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * The runs of joined exercises in a section, as index arrays.
 *
 * `superset_with_next` is a per-ROW boolean, so a run is a maximal stretch of rows each flagged
 * to join the next, plus the row that stretch ends on. Single exercises come back as runs of one,
 * so a caller never has to hold two shapes.
 *
 * ⚠️ A FLAG ON THE LAST ROW MAKES NO GROUP, and that is V6-12's finding rather than an edge case:
 * nothing follows it, and rule 14 rejects a one-member superset as a section the athlete cannot
 * execute. It is reported by `validate` and ignored here.
 */
export function supersetRuns(exercises: readonly Pick<ExerciseDraft, "supersetWithNext">[]): number[][] {
  const runs: number[][] = [];
  let current: number[] = [];
  exercises.forEach((ex, i) => {
    current.push(i);
    const last = i === exercises.length - 1;
    if (!ex.supersetWithNext || last) { runs.push(current); current = []; }
  });
  return runs;
}

/** One exercise's sets after the owner's rule has been applied to the run it sits in. */
export type LevelledExercise = { exercise: ExerciseDraft; surplus: SetDraft[] };

/**
 * Level a joined run DOWN to its shortest member, returning each member's surplus separately.
 *
 * The surplus is the caller's to place as a trailing solo row — this function does not insert it,
 * because where it goes is a question about the SECTION's order and this one is about the run.
 *
 * A run of one is returned untouched with no surplus: a solo exercise is not a pair and levelling
 * it against itself would be a rule firing where there is nothing to reconcile.
 */
export function levelRunDown(run: ExerciseDraft[]): LevelledExercise[] {
  if (run.length <= 1) return run.map((exercise) => ({ exercise, surplus: [] }));
  const shortest = Math.min(...run.map((e) => e.sets.length));
  return run.map((exercise) => ({
    exercise: { ...exercise, sets: exercise.sets.slice(0, shortest) },
    surplus: exercise.sets.slice(shortest),
  }));
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════
 * VALIDATION — and it enables the Save button rather than greying it out.
 *
 * §8i recorded that the greyed-out primary button appears THREE times in their product, and §1d
 * called it *"the first place in the map where copying them would make ours worse"*: a disabled
 * button cannot say why it is disabled, and a keyboard user cannot even reach it to find out.
 * The web app's sign-in screen already decided this — validate on press, name the field that is
 * wrong, move focus to it. The builder follows the screen that came first.
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

export type Problem = {
  /** Where it is, so the screen can move focus rather than describing a location in prose. */
  sectionIndex: number | null;
  exerciseIndex: number | null;
  message: string;
};

export function validate(draft: WorkoutDraft): Problem[] {
  const problems: Problem[] = [];
  if (draft.name.trim() === "") {
    problems.push({ sectionIndex: null, exerciseIndex: null, message: "The workout needs a name." });
  }
  if (draft.sections.length === 0) {
    /* 🔴 W32(a) CHANGED WHAT THIS SENTENCE SHOULD SAY, AND LEAVING IT WOULD HAVE POINTED THE
       COACH AT THE WRONG BUTTON. It read "Add at least one section." from the days when a
       section was the only container an exercise could enter. An empty workout now accepts an
       exercise directly — the add lands loose and makes its own implicit section — so the
       shortest true route out of this state is to add an EXERCISE, and a section is one way of
       getting there rather than the requirement.
       ⚠️ The refusal itself is unchanged: an empty workout is still not saveable, and this is
       still a blocking problem. What moved is only the instruction, which is the half a coach
       actually acts on. */
    problems.push({ sectionIndex: null, exerciseIndex: null, message: "Add at least one exercise." });
  }
  draft.sections.forEach((section, si) => {
    if (section.exercises.length === 0) {
      problems.push({ sectionIndex: si, exerciseIndex: null, message: "This section has no exercises." });
    }
    // A clock-run section without its number is a section the phone cannot run: an AMRAP with no
    // cap counts up for ever. Refused here, by the field's own name, rather than discovered by
    // the athlete.
    for (const field of formatConfigProblems(section.format, section.formatConfig)) {
      problems.push({
        sectionIndex: si, exerciseIndex: null,
        message: `This ${section.format.replace(/_/g, " ")} section needs its ${field.label.toLowerCase()}.`,
      });
    }
    section.exercises.forEach((ex, ei) => {
      if (ex.sets.length === 0) {
        problems.push({ sectionIndex: si, exerciseIndex: ei, message: `${ex.name} has no sets.` });
      }
      // The order rule at the door (his 2026-09-11 word): the select withholds it; this catches a
      // row typed in another order, and says which one.
      const fault = rungAfterWorkFault(ex.sets);
      if (fault !== null) {
        problems.push({ sectionIndex: si, exerciseIndex: ei, message: `${ex.name}: set ${fault + 1} is a warm-up after a working set. ${SET_ORDER_RULE}` });
      }
    });
    // ⚠️ A JOIN FLAG ON THE LAST ROW IS V6-12's DEFECT, NOT A TYPO TO ABSORB. Nothing follows it,
    // so it would reach the athlete as a one-member superset — which rule 14 rejects critically.
    const last = section.exercises[section.exercises.length - 1];
    if (last !== undefined && last.supersetWithNext) {
      problems.push({
        sectionIndex: si, exerciseIndex: section.exercises.length - 1,
        message: `${last.name} is joined to the next exercise, but nothing follows it.`,
      });
    }
    // ⚠️ A PAIR IN A FORMAT THAT CANNOT HOLD ONE. EMOM decides when each exercise starts and
    // `for_time` is one clock over the whole block, so "no rest between these two" has nothing
    // to mean inside either.
    if (!allowsSupersets(section.format) && section.exercises.some((e) => e.supersetWithNext)) {
      problems.push({
        sectionIndex: si, exerciseIndex: null,
        message: `Exercises are joined, but a ${section.format.replace(/_/g, " ")} section cannot hold a superset.`,
      });
    }
    // The owner's split rule is applied at save; an unequal pair is reported here so the coach
    // learns what will happen rather than discovering a trailing row afterwards.
    supersetRuns(section.exercises).forEach((run) => {
      if (run.length < 2) return;
      // HIS RUNG RULE: a warm-up set on a joined exercise moves to its own row before the
      // superset at save (said here, not discovered); a joined exercise that is ONLY warm-up sets
      // has no working set to pair and is refused — nothing sensible can be written for it.
      run.forEach((i) => {
        const ex = section.exercises[i];
        if (ex === undefined) return;
        const rungs = ex.sets.filter((s) => s.type === "warm_up").length;
        if (rungs === 0) return;
        if (rungs === ex.sets.length) {
          problems.push({
            sectionIndex: si, exerciseIndex: i,
            message: `${ex.name} is joined to a superset but has only warm-up sets — add a working set, or unjoin it.`,
          });
        } else {
          problems.push({
            sectionIndex: si, exerciseIndex: i,
            message: `${ex.name} has ${rungs} warm-up ${rungs === 1 ? "set" : "sets"} inside a superset — they will move to their own row before the superset.`,
          });
        }
      });
      // The pair is levelled on WORKING sets — rungs have already left it by then.
      const counts = run.map((i) => section.exercises[i]?.sets.filter((s) => s.type !== "warm_up").length ?? 0);
      if (new Set(counts).size > 1) {
        const firstIndex = run[0];
        problems.push({
          sectionIndex: si, exerciseIndex: firstIndex ?? null,
          message: `Joined exercises have ${counts.join(" and ")} sets — they will be evened to ${Math.min(...counts)}, and the extra sets become a separate row after the superset.`,
        });
      }
    });
  });
  return problems;
}

/** A problem that stops a save, as opposed to one that only warns. The unequal-pair notice is a
 *  description of what saving will DO, so it must not block the save it is describing. */
export function blocking(problems: Problem[]): Problem[] {
  return problems.filter((p) => !p.message.includes("they will be evened to")
    && !p.message.includes("they will move to their own row"));
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════
 * THE SAVE PLAN — and the ordering is forced by two measurements, not chosen.
 *
 * 🔴 MEASUREMENT ONE: `coach_assignments.workout_id → coach_workouts` IS **ON DELETE CASCADE**.
 * So deleting a workout in order to re-create it DELETES EVERY ASSIGNMENT OF IT — including past
 * days the athlete already trained. This repo has paid for that exact shape once already: the
 * 2026-08-27 ghost-session cleanup deleted `workout_session_exercises` rows and took a proven
 * `form_clips` row with them, leaving an orphaned 4 MB file and a claim that had taken three
 * sessions to close. **An edit therefore UPDATES the workout row and never replaces it**, and
 * `planSave` has no step that can delete one — the type has no such case.
 *
 * MEASUREMENT TWO: the children may be replaced wholesale, and this is what licenses it.
 *   - `sections → workouts`, `exercises → sections`, `sets → exercises` are all ON DELETE
 *     CASCADE, so deleting a workout's sections removes its exercises and sets with them: the
 *     replace is ONE delete, not three.
 *   - **NOTHING OUTSIDE THE TREE REFERENCES ANY OF THE THREE CHILD TABLES.** Measured over
 *     `pg_constraint`: the only inbound foreign keys are the tree's own parent links plus
 *     `coach_assignments.workout_id` / `root_workout_id`, which point at the WORKOUT. So a
 *     replaced section id cannot dangle anywhere. This is also §8.1's live finding from the
 *     other side — `workout_session_exercises.coach_exercise_id` resolves to `coach_exercises`
 *     on all 332 populated rows and to `coach_workout_exercises` on ZERO.
 *
 * ⚠️ AND EDITING A TEMPLATE CANNOT REACH A CLIENT'S COPY, MEASURED RATHER THAN ASSUMED. V7-2's
 * copy-on-assign gives every assignment its own client-scoped workout; of the 70 sections
 * belonging to his client copies, **ZERO belong to a template**. Every write below is scoped by
 * one workout id, so the isolation is structural rather than a rule somebody has to remember.
 *
 * ⚠️ THE STEPS RUN ONE AT A TIME, AND RLS IS WHY — this was measured, not assumed. The policies
 * on all three child tables are `EXISTS (… JOIN coach_workouts w … w.coach_id = auth.uid())`,
 * and a row inserted earlier in the SAME statement is not visible to that subquery. A chained-CTE
 * insert of the whole tree was **REFUSED 42501 on `coach_workout_sections`**; the identical
 * inserts as separate statements were ACCEPTED. So the parent must land before its children are
 * offered, and `Promise.all` over the tree is the obvious tidy-up and is wrong — the same
 * conclusion `AssignPanel` reached for a different reason.
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

export type SaveStep =
  | { kind: "insertWorkout"; name: string; description: string | null }
  | { kind: "updateWorkout"; workoutId: string; name: string; description: string | null }
  /** Removes the whole child tree by cascade. Never touches the workout row or any client copy. */
  | { kind: "deleteSections"; workoutId: string }
  | { kind: "insertSection"; position: number; section: SectionDraft }
  | { kind: "insertExercise"; sectionPosition: number; position: number; exercise: ExerciseDraft }
  | { kind: "insertSets"; sectionPosition: number; exercisePosition: number; sets: SetDraft[] };

/**
 * What saving this draft will do, in order.
 *
 * ⚠️ POSITIONS ARE ZERO-BASED, AND THAT IS THE "ONE ORDINAL, TWO BASES" TRAP THIS REPO ALREADY
 * PAID FOR. Every coach-side position is 0-based (`coach_workout_sections` 0..10,
 * `coach_workout_exercises` 0..11, `coach_workout_sets` 0..9) while `workout_session_exercises
 * .position` and `set_logs.set_number` are 1-based — the defect that made `tools/v7-relog-week`
 * fail its rehearsal. Emitting 1-based here would compile, save, render, and pair against nothing.
 *
 * The owner's superset rule is applied HERE rather than in the editor, so what is written is
 * always the levelled shape: a coach may leave a run unequal while thinking, and the surplus
 * becomes a trailing solo row at the moment it is stored.
 */
export function planSave(draft: WorkoutDraft): SaveStep[] {
  const steps: SaveStep[] = [];
  if (draft.id === null) {
    steps.push({ kind: "insertWorkout", name: draft.name.trim(), description: draft.description });
  } else {
    steps.push({ kind: "updateWorkout", workoutId: draft.id, name: draft.name.trim(), description: draft.description });
    steps.push({ kind: "deleteSections", workoutId: draft.id });
  }
  draft.sections.forEach((section, si) => {
    const resolved = resolveSupersets(section);
    steps.push({ kind: "insertSection", position: si, section: resolved });
    resolved.exercises.forEach((ex, ei) => {
      steps.push({ kind: "insertExercise", sectionPosition: si, position: ei, exercise: ex });
      steps.push({ kind: "insertSets", sectionPosition: si, exercisePosition: ei, sets: ex.sets });
    });
  });
  return steps;
}

/**
 * A section with the owner's superset rule applied: every joined run levelled DOWN to its
 * shortest member, and each member's surplus placed as a solo row immediately after the run.
 *
 * "After the run" is his own wording — *"there will be added 1 more set alone after the
 * superset"* — and it is also the only placement that keeps the section's reading order honest:
 * the surplus is work that happens once the pair is finished.
 *
 * ⚠️ THE SURPLUS ROW IS NOT JOINED TO ANYTHING. It carries `supersetWithNext: false` explicitly,
 * because inheriting the flag from the row it came from would make it a THIRD member of the run
 * it was just split out of — which is precisely what V6-5 recorded rule 14 rejecting critically.
 */
export function resolveSupersets(section: SectionDraft): SectionDraft {
  const out: ExerciseDraft[] = [];
  for (const run of supersetRuns(section.exercises)) {
    const joined = run.map((i) => section.exercises[i]).filter((e): e is ExerciseDraft => e !== undefined);
    // HIS RUNG RULE (2026-09-04): *"all warm up sets separate before the superset, as an
    // exercise … in my whole week program and also kept as a rule for the engine."* The phone
    // performs a joined pair's rungs on their own page before the pair (`SupersetRounds`), the
    // week was rewritten that way (`tools/week-rungs-20260904/`), and the builder writes the same
    // shape: each member's warm-up sets become a solo row placed BEFORE the run, in member order,
    // and the run is levelled on WORKING sets only — a rung must never count toward the pair's
    // round count (the trap `levelSupersetSets` fell into on the phone, 2026-09-03).
    const split = run.length >= 2 ? joined.map(splitRungs) : joined.map((e) => ({ rungs: [], exercise: e }));
    for (const { rungs, exercise } of split) {
      if (rungs.length > 0) out.push({ ...exercise, supersetWithNext: false, sets: rungs });
    }
    const members = split.map((s) => s.exercise);
    const levelled = levelRunDown(members);
    for (const { exercise } of levelled) out.push(exercise);
    for (const { exercise, surplus } of levelled) {
      if (surplus.length === 0) continue;
      out.push({ ...exercise, supersetWithNext: false, sets: surplus });
    }
  }
  return { ...section, exercises: out };
}

/**
 * A joined exercise's warm-up sets, and the exercise without them. An exercise whose sets are ALL
 * warm-up is left whole — `validate` refuses that shape rather than emptying a member of the run.
 */
export function splitRungs(exercise: ExerciseDraft): { rungs: SetDraft[]; exercise: ExerciseDraft } {
  const rungs = exercise.sets.filter((s) => s.type === "warm_up");
  const working = exercise.sets.filter((s) => s.type !== "warm_up");
  if (rungs.length === 0 || working.length === 0) return { rungs: [], exercise };
  return { rungs, exercise: { ...exercise, sets: working } };
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════
 * THE LIBRARY LIST — moved out of the screen, because a `.map()` in a component is the one
 * fallible thing in this app with no runner.
 *
 * Session 2 paid for this exact shape: a PostgREST embed mapped inline in `Client.tsx` where
 * nothing could execute it, hiding a trap that would have rendered a whole column as "Untitled
 * workout" with nothing red. The rule that came out of it — *when a screen's only untested code
 * is a shape conversion, that is exactly where a silent-blank defect lives* — applies here twice
 * over, since this one both reshapes an embed AND counts it.
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

/** A workout row as PostgREST returns it, with the counts still nested. */
export type LibraryWorkoutRow = {
  id: string;
  name: string;
  description: string | null;
  coach_workout_sections?: {
    id: string;
    name: string | null;
    position: number;
    /** `exercise_id` is the LIBRARY row this prescribes — optional, because a caller that does
     *  not ask for it gets `unknownRows` from `missingRuleCount` rather than a wrong count. */
    coach_workout_exercises?: { id: string; exercise_id?: string }[] | null;
  }[] | null;
};

export type AssignmentRow = {
  workout_id: string | null;
  root_workout_id: string | null;
  scheduled_date: string | null;
};

export type LibraryRow = {
  id: string; name: string; description: string | null;
  sections: number; exercises: number; lastUsedISO: string | null;
  /** The workout's sections IN ORDER, so a row can name its shape rather than count it. */
  shape: SectionCount[];
  /** Every library exercise this template prescribes, one entry per ROW (duplicates kept — the
   *  same movement seated twice is two rows that will not progress). Empty when the caller did
   *  not select `exercise_id`, which reads as UNKNOWN downstream and never as "none missing". */
  exerciseIds: string[];
};

/**
 * The most recent day each TEMPLATE was assigned for.
 *
 * ⚠️ KEYED ON `coalesce(root_workout_id, workout_id)` — the same expression the unique index and
 * `assign.ts` use, and it has to be. V7-2's copy-on-assign means `workout_id` names a private
 * client COPY, never the template, so keying on it would give every template "never assigned"
 * while twelve assignments of it existed. Measured live: all 12 carry a root pointing at a
 * template, a `workout_id` pointing at a copy, and the two are NEVER equal. The coalesce is what
 * still reads a legacy row written before the backfill.
 */
export function lastUsedByTemplate(assignments: AssignmentRow[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const a of assignments) {
    const key = a.root_workout_id ?? a.workout_id;
    if (key === null || a.scheduled_date === null) continue;
    const seen = out.get(key);
    // ISO dates sort lexicographically, so the newest wins without parsing anything.
    if (seen === undefined || a.scheduled_date > seen) out.set(key, a.scheduled_date);
  }
  return out;
}

/**
 * The library rows a coach reads, counts and all.
 *
 * ⚠️ A MISSING EMBED IS ZERO, NOT A CRASH. PostgREST omits an embedded array entirely rather than
 * returning `[]` when there is nothing to embed, and `.length` on undefined throws — which in a
 * screen is a blank page rather than a wrong number.
 */
export function libraryRows(workouts: LibraryWorkoutRow[], assignments: AssignmentRow[]): LibraryRow[] {
  const lastUsed = lastUsedByTemplate(assignments);
  return workouts.map((w) => {
    // ⚠️ SORTED BY `position`, BECAUSE THE QUERY DOES NOT. A PostgREST nested embed comes back in
    // no guaranteed order, and session 3 paid for exactly this twice over — two mutations
    // survived there because every fixture happened to arrive already ordered. Counting is blind
    // to order and NAMING is not, so the moment a row lists its sections the sort stops being
    // theoretical: it would print a coach's cool-down first with nothing to notice.
    const sections = [...(w.coach_workout_sections ?? [])].sort((a, b) => a.position - b.position);
    return {
      id: w.id,
      name: w.name,
      description: w.description,
      sections: sections.length,
      exercises: sections.reduce((n, s) => n + (s.coach_workout_exercises?.length ?? 0), 0),
      lastUsedISO: lastUsed.get(w.id) ?? null,
      shape: sections.map((s) => ({
        name: s.name,
        exercises: s.coach_workout_exercises?.length ?? 0,
        /* ⚠️ THE SAME DROP RULE AS `exerciseIds` BELOW, and it is why `exercises` is counted off
           the ROWS rather than off this list: a row with no `exercise_id` is still an exercise
           in the section and must still be counted — it just cannot be named. The count and the
           nameable ids are two different numbers and both are honest. */
        exerciseIds: (s.coach_workout_exercises ?? [])
          .flatMap((e) => (e.exercise_id ? [e.exercise_id] : [])),
      })),
      /* ⚠️ A ROW WITH NO `exercise_id` IS DROPPED, NOT DEFAULTED TO "". An empty-string id would
         miss every entry in the rule map and be counted as UNKNOWN anyway — but it would do so
         by accident, and a caller reading `exerciseIds.length` would be told the template has
         rows it cannot name. Dropping makes the two numbers honest: what we know, and nothing
         else. */
      exerciseIds: sections.flatMap((s) =>
        (s.coach_workout_exercises ?? []).flatMap((e) => (e.exercise_id ? [e.exercise_id] : []))),
    };
  });
}

/**
 * The last-used badge, in the units a coach thinks in.
 *
 * Weeks past a fortnight, because "37 days ago" is a number you convert and "5w" is one you read
 * — their own badge does the same and §8a's KEEP verdict is about exactly this.
 *
 * ⚠️ NEVER-ASSIGNED SAYS SO RATHER THAN RENDERING BLANK. A blank reads as "assigned, date
 * unknown", and the two are different facts about a template a coach is deciding whether to keep.
 * This is the roster's own near-miss in miniature: a silently empty cell is indistinguishable
 * from a real absence.
 */
export function describeAge(days: number | null): string {
  if (days === null) return "Never assigned";
  // A future assignment is real — a coach plans ahead — and "Used -3d ago" is nonsense.
  if (days < 0) return "Assigned ahead";
  if (days === 0) return "Used today";
  if (days === 1) return "Used yesterday";
  if (days < 14) return `Used ${days}d ago`;
  if (days < 365) return `Used ${Math.floor(days / 7)}w ago`;
  return "Used over a year ago";
}

/* ⚠️ `describeSize` IS DELETED, AND ITS REASON IS RE-HOMED RATHER THAN DELETED WITH IT.
 * It rendered `20 exercises · 9 sections` and session 10 replaced that row with `describeShape`,
 * which names the sections instead — so it had zero callers and was kept alive only by its own
 * test, which is the write-only-member shape this repo deletes on sight. What it was RIGHT about
 * is pluralisation: a list a coach reads two hundred times is where "1 exercises" accumulates
 * into a product looking unfinished, and `describeShape` pluralises the total it states for
 * exactly that reason. */

/* ── Reordering ────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 THE BUILDER SHIPPED WITH NO WAY TO REORDER ANYTHING, AND THAT WAS A HOLE RATHER THAN A
 * DECISION. Measured before writing this: `Move up`, `Move down`, `moveExercise` and `onMove`
 * appeared ZERO times in both `WorkoutBuilder.tsx` and this file. A coach could add an exercise
 * and delete an exercise and could not put one before another by any means, mouse or keyboard.
 * The owner found it from the outside — *"ours look infantile as it is now"* — which is the
 * honest verdict on a builder you cannot rearrange.
 *
 * ⚠️ AND THE ANSWER IS NOT TO COPY THEIR DRAG-AND-DROP, WHICH THE MAP ALREADY MEASURED AS THE
 * WEAK HALF OF THEIR BUILDER. `EVERFIT-MAP.md` §12: their composition is drag-first, their
 * training calendar contains ZERO focusable elements, their add affordance exists only on hover
 * — and **they shipped a separate "Workout Arrangement" panel purely for reordering, which is
 * itself the symptom: drag-reorder does not scale to a 24-exercise session even with a mouse.**
 * So the buttons come first and are the complete path; a drag gesture on top of them would be an
 * ACCELERATOR, and it may never be the only way in.
 */

/**
 * Move one exercise within its section.
 *
 * ⚠️ MOVING A ROW TAKES IT OUT OF ITS SUPERSET, AND THAT IS THE WHOLE DESIGN DECISION HERE.
 * `supersetWithNext` describes the BOUNDARY between a row and the one after it, not the row —
 * so a naive swap silently rewrites which exercises are paired. Concretely: with `A—B` joined
 * and `C` loose, moving `C` up produces `A—C` joined and `B` loose, a pairing nobody asked for.
 * That is not cosmetic: rule 14 refuses a run it does not like, `levelRunDown` applies the
 * owner's split-down rule to whatever run it finds, and the athlete then performs two exercises
 * back to back that the coach never put together.
 *
 * So a move UNJOINS the row it moves — its own flag and the flag pointing AT it are both
 * cleared. The direction is the safe one, and it is the same asymmetry `EngineRestCeiling`
 * ships under: the worst case is a pairing the coach has to make again, visibly, rather than one
 * that appears without being asked for. Re-joining is one click on the control that exists.
 *
 * An index at the end of its travel returns the section UNCHANGED rather than throwing, so a
 * control that cannot move is inert rather than dangerous — and the screen disables it anyway.
 */
export function moveExercise(section: SectionDraft, index: number, delta: -1 | 1): SectionDraft {
  const to = index + delta;
  if (index < 0 || index >= section.exercises.length) return section;
  if (to < 0 || to >= section.exercises.length) return section;

  const rows = section.exercises.map((e) => ({ ...e }));

  const moved = rows[index];
  const target = rows[to];
  if (moved === undefined || target === undefined) return section;

  // ⚠️ THERE ARE THREE FLAGS TO CLEAR, NOT TWO, AND THE FIRST DRAFT OF THIS CLEARED TWO. The
  // row's own flag and the one pointing at it where it CAME FROM are the obvious pair; the one
  // that matters is the third — the flag pointing at it where it LANDS. Without it, moving C up
  // past B produced exactly the defect the rule exists to prevent: `A—C` joined, a pairing
  // nobody asked for. The doc above said "the flag pointing AT it" and the code answered that
  // for the old position only, which is the same sentence being true of the wrong coordinates.
  // Caught by its own test, which is why the test states the flags and not just the order.
  moved.supersetWithNext = false;
  const oldPredecessor = rows[index - 1];
  if (oldPredecessor !== undefined) oldPredecessor.supersetWithNext = false;

  rows[index] = target;
  rows[to] = moved;

  // AFTER the swap, so `to - 1` is the row that now genuinely precedes the moved one.
  const newPredecessor = rows[to - 1];
  if (newPredecessor !== undefined) newPredecessor.supersetWithNext = false;

  return { ...section, exercises: rows };
}

/* ── THE LOOSE ROW — §6.5, his word: "why not have that freedom?" ─────────────────────────
 *
 * A row dropped BETWEEN two sections, above the first or below the last does not become
 * section-less — nothing in either platform can read that. It gets an IMPLICIT section: a real
 * `coach_workout_sections` row carrying `is_implicit = true`, which the web draws without a bar
 * and a coach never named.
 *
 * ⚠️ STRAIGHT SETS AND THE `workout` BLOCK, NOT A COPY OF WHERE THE ROW CAME FROM. An implicit
 * section cannot be customised, so every value on it must be one nobody needs to change: a circuit
 * inherited from the row's old home would carry rounds and rest a coach could never see or edit.
 */
export function newImplicitSection(exercises: ExerciseDraft[]): SectionDraft {
  return {
    name: null, format: "straight_sets", sectionType: "workout", description: null,
    formatConfig: {}, isImplicit: true, exercises,
  };
}

/**
 * Drop a run BETWEEN sections — at section-boundary `at` (0 = above the first, length = below the
 * last). The run leaves its old section and lands in a new implicit one.
 *
 * ⚠️ IT REUSES `moveExerciseTo` RATHER THAN MOVING ROWS ITSELF, which is what keeps §6.4's rule (c)
 * true for this gesture too: the empty implicit section is created FIRST, then the ordinary move
 * puts the run into it. One mover, three gestures.
 */
export function dropLooseAt(
  sections: readonly SectionDraft[],
  from: { section: number; index: number; count?: number },
  at: number,
): SectionDraft[] {
  const src = sections[from.section];
  if (src === undefined) return [...sections];
  const span = Math.max(1, from.count ?? 1);
  if (from.index < 0 || from.index >= src.exercises.length) return [...sections];

  const boundary = Math.max(0, Math.min(Math.trunc(at), sections.length));
  const withHole: SectionDraft[] = [
    ...sections.slice(0, boundary), newImplicitSection([]), ...sections.slice(boundary),
  ];
  // The source index shifts by one if the new section was inserted above it.
  const fromSection = from.section + (boundary <= from.section ? 1 : 0);
  const moved = moveExerciseTo(withHole, { section: fromSection, index: from.index, count: span },
                               { section: boundary, index: 0 });
  return tidyImplicit(moved);
}

/**
 * W80 (Fable's add) — WHERE A MOVED ROW LANDED, found in the result rather than re-derived.
 *
 * After a keyboard move (`⋯ → Move up / down / to / out`) focus fell to `<body>` (MEASURED 2026-09-25), so a keyboard
 * coach lost their place. The page returns focus to the moved row's `⋯`, and needs its new coordinates. The movers copy
 * rows (`{ ...e }`), so there is no identity to follow — and computing the landing arithmetically would re-state each
 * mover's rules (the span correction, `tidyImplicit`'s deletions and merges) a second time. So this FINDS it: the row
 * whose content equals the moved one (`supersetWithNext` aside — a move may clear it). With identical twins in the
 * workout, the occurrence at a place no twin stood before is taken; failing that, the same ordinal among the twins.
 */
export function landedAt(
  before: readonly SectionDraft[],
  after: readonly SectionDraft[],
  from: { section: number; index: number },
): { section: number; index: number } | null {
  const key = (e: ExerciseDraft) => JSON.stringify({ ...e, supersetWithNext: false });
  const moved = before[from.section]?.exercises[from.index];
  if (moved === undefined) return null;
  const k = key(moved);
  const where = (ss: readonly SectionDraft[]) => ss.flatMap((s, si) =>
    s.exercises.flatMap((e, ei) => (key(e) === k ? [{ section: si, index: ei }] : [])));
  const was = where(before), now = where(after);
  if (now.length <= 1) return now[0] ?? null;
  const fresh = now.filter((p) => !was.some((q) => q.section === p.section && q.index === p.index));
  if (fresh.length === 1) return fresh[0]!;
  const ordinal = was.findIndex((q) => q.section === from.section && q.index === from.index);
  return now[ordinal] ?? now[0]!;
}

/**
 * Delete implicit sections nothing lives in, and merge adjacent ones.
 *
 * ⚠️ AN IMPLICIT SECTION IS NOT A THING A COACH MADE, so an empty one is not an empty section they
 * are about to fill — it is a container whose reason for existing has left. An EXPLICIT empty
 * section is the opposite and is never touched here: a coach who adds a section and has not yet
 * put anything in it is mid-thought.
 *
 * ⚠️ ADJACENT IMPLICIT SECTIONS MERGE, because two of them in a row are indistinguishable on
 * screen from one — they draw no bar — so leaving them separate would store a distinction nobody
 * can see and nobody asked for.
 */
export function tidyImplicit(sections: readonly SectionDraft[]): SectionDraft[] {
  const kept = sections.filter((s) => !(s.isImplicit && s.exercises.length === 0));
  const out: SectionDraft[] = [];
  for (const sec of kept) {
    const last = out[out.length - 1];
    if (sec.isImplicit && last !== undefined && last.isImplicit) {
      out[out.length - 1] = { ...last, exercises: [...last.exercises, ...sec.exercises] };
      continue;
    }
    out.push(sec);
  }
  return out;
}

/* ── MOVING AN EXERCISE ANYWHERE — ONE FUNCTION, TWO CALLERS ───────────────────────────────
 *
 * 🔴 §6.4 RULE: "the outline's drag calls the SAME move handlers the cards' arrows call — no
 * second write path." ⚠️ THAT HANDLER DID NOT EXIST FOR A CROSS-SECTION MOVE: `moveExercise` takes
 * ONE section and a ±1 delta, so it cannot express "out of section 2 and into section 5". Rather
 * than add a second mover beside it — which is exactly the drift the rule forbids — this is the
 * general move, and the arrows become a special case of it (same section, index ± 1).
 *
 * 🔴 THE PAIR IS A LINK BETWEEN NEIGHBOURS, SO THREE FLAGS CAN LIE AFTER A MOVE, AND
 * `moveExercise`'s own comment records paying for missing the third: the row's own flag, the flag
 * where it CAME FROM, and the flag where it LANDS. A cross-section move has the same three, in two
 * different sections. The cheapest correct way to get all of them is not to reason about indices
 * at all: REMOVE the row, then INSERT it, and re-seal both sections afterwards — `seal` clears the
 * trailing flag and any flag that now points across the gap the row left or made.
 *
 * `count` moves a RUN: a linked pair travels as one object (§6.4), and the rows after the first
 * keep their own flags so the pair survives the journey.
 */
export function moveExerciseTo(
  sections: readonly SectionDraft[],
  from: { section: number; index: number; count?: number },
  to: { section: number; index: number },
): SectionDraft[] {
  const src = sections[from.section];
  if (src === undefined) return [...sections];
  const span = Math.max(1, from.count ?? 1);
  if (from.index < 0 || from.index >= src.exercises.length) return [...sections];
  const moving = src.exercises.slice(from.index, from.index + span).map((e) => ({ ...e }));
  if (moving.length === 0) return [...sections];
  // The run travels whole, so its LAST row can never keep a link to what it no longer precedes.
  moving[moving.length - 1]!.supersetWithNext = false;

  // 1. take it out
  const without = sections.map((sec, i) => (i !== from.section
    ? sec
    : { ...sec, exercises: sec.exercises.filter((_, j) => j < from.index || j >= from.index + span) }));

  // 2. put it back, with the landing index expressed against the list AFTER the removal — which
  //    is what makes a move within one section unambiguous rather than off by the span.
  let at = to.index;
  if (to.section === from.section && to.index > from.index) at = to.index - span;
  const target = without[to.section];
  if (target === undefined) return [...sections];
  at = Math.max(0, Math.min(Math.trunc(at), target.exercises.length));

  const placed = without.map((sec, i) => (i !== to.section
    ? sec
    : { ...sec, exercises: [...sec.exercises.slice(0, at), ...moving, ...sec.exercises.slice(at)] }));

  // 3. re-seal BOTH sections. Only these two can have changed, and sealing a section the move did
  //    not touch would clear a coach's pair for no reason.
  return placed.map((sec, i) => (i === from.section || i === to.section ? seal(sec, from, to, at, span, i) : sec));
}

/** Clear the flags a move can have left pointing at the wrong neighbour. */
function seal(
  sec: SectionDraft,
  from: { section: number; index: number },
  to: { section: number; index: number },
  at: number,
  span: number,
  which: number,
): SectionDraft {
  const rows = sec.exercises.map((e) => ({ ...e }));
  const clear = (i: number) => { const r = rows[i]; if (r !== undefined) r.supersetWithNext = false; };
  // Where the run was taken FROM: the row that preceded it now touches what followed it. 🔴 FOUND 2026-09-30 (his report
  // after the W96 deploy: "supersets are breaking up when releasing sometimes"): that row is read in the list AS IT IS NOW.
  // A run that landed ABOVE its old place in the same part pushed it down by `span`, and clearing `from.index - 1` there
  // cut whichever superset had shifted into that slot (MEASURED: `A B C+D E`, E to the top → `E A B C D`).
  if (which === from.section) clear(which === to.section && at < from.index ? from.index - 1 + span : from.index - 1);
  // Where the run LANDED: the row it now sits behind can no longer claim the row it used to touch.
  if (which === to.section) clear(at - 1);
  // A trailing row can never be joined to a row that does not exist.
  if (rows.length > 0) clear(rows.length - 1);
  return { ...sec, exercises: rows };
}

/**
 * Move one section within the workout.
 *
 * Simpler than an exercise by exactly one thing: a section carries no flag describing its
 * relationship to the next one, so there is nothing that can be silently rewritten. Order is
 * the whole of what changes — which is also why `planSave` renumbers positions from the array
 * rather than storing them.
 */
export function moveSection(draft: WorkoutDraft, index: number, delta: -1 | 1): WorkoutDraft {
  const to = index + delta;
  if (index < 0 || index >= draft.sections.length) return draft;
  if (to < 0 || to >= draft.sections.length) return draft;

  const sections = [...draft.sections];
  const moved = sections[index];
  const target = sections[to];
  if (moved === undefined || target === undefined) return draft;
  sections[index] = target;
  sections[to] = moved;
  return { ...draft, sections };
}

/**
 * Whether a move is available — so a screen can disable a control rather than offer one that
 * does nothing.
 *
 * ⚠️ A DISABLED BUTTON AND A BUTTON THAT SILENTLY DOES NOTHING ARE DIFFERENT THINGS TO A
 * KEYBOARD USER. The second is the shape `EVERFIT-MAP.md` §29.8 records in their own flyout
 * header — a control that advertises an action it does not have. This is the one predicate both
 * the buttons and the tests ask, so the answer cannot differ between them.
 */
export function canMove(count: number, index: number, delta: -1 | 1): boolean {
  const to = index + delta;
  return index >= 0 && index < count && to >= 0 && to < count;
}

/* ── Duplicating a workout, and where a picked exercise lands ──────────────────────────────
 *
 * Session 9. Two features, and both are one question each: *where does a new workout start
 * from*, and *which section does a clicked exercise go into*. The owner asked for the first in
 * his own words — *"they got to choose create from template or new workout"* — and the second is
 * the design question the brief flagged, because Everfit answers it by where you DROP and a drag
 * gesture may never be the only path here (`EVERFIT-MAP.md` §12 measured their primary work
 * surface at ZERO focusable elements).
 */

/**
 * The name a duplicate gets.
 *
 * ⚠️ A DUPLICATE MUST NOT SHARE ITS SOURCE'S NAME, and that is not politeness. The library is
 * `.order("name")`, so two rows reading `Push A` sit adjacent and identical, and the coach's next
 * act is to open one and edit it — with no way to know which. The rename is what makes the second
 * row a thing you can aim at.
 *
 * ⚠️ AND IT DOES NOT NEST. Duplicating `Push A (copy)` gives `Push A (copy 2)`, never
 * `Push A (copy) (copy)`: the suffix is stripped from the source first, so the names stay a flat
 * numbered family however many times a coach presses the button. A coach who really has a workout
 * called `Copycat (copy)` still gets a correct answer — the strip yields `Copycat`, the first free
 * name is computed from there, and nothing is lost because the ORIGINAL row is untouched either
 * way.
 *
 * ⚠️ THE COLLISION TEST IS CASE-INSENSITIVE ON PURPOSE, and it can only ever move the number
 * FORWARD. Names are typed by a person, so `push a (copy)` and `Push A (copy)` are two rows that
 * read as one in a sorted list. Comparing exactly would let this mint the second of a
 * visually-identical pair; comparing loosely picks a further-along number, which is never wrong
 * and is occasionally generous. It never renames or touches an existing row.
 */
/** William's item 4 — the name a client's copy takes when it is SAVED TO THE LIBRARY: its own while no template holds it
 *  (case-blind, as `duplicateName` compares), `duplicateName`'s `… (copy)` ladder otherwise. */
export function libraryName(existing: readonly string[], source: string): string {
  const own = source.trim();
  const taken = new Set(existing.map((n) => n.trim().toLowerCase()));
  return own !== "" && !taken.has(own.toLowerCase()) ? own : duplicateName(existing, source);
}

export function duplicateName(existing: readonly string[], source: string): string {
  const taken = new Set(existing.map((n) => n.trim().toLowerCase()));
  const base = stripCopySuffix(source.trim());
  const free = (candidate: string) => !taken.has(candidate.toLowerCase());

  // ⚠️ THE 28-CHARACTER CAP HOLDS FOR A DUPLICATE TOO (W84-1's residue, 2026-09-27): the builders refuse the 29th character
  // typed or pasted, and a copied 28-character name used to come back as 35. `fitWorkoutName` (generated from the phone's
  // `WorkoutNameCap.fitting`) keeps the suffix whole and cuts the base with `…`, and the collision test runs on the FITTED
  // name — the one that is actually written.
  const first = fitWorkoutName(base, " (copy)");
  if (free(first)) return first;
  // From 2 upward. The bound is not a policy — it is a refusal to loop forever on a set somebody
  // could have made pathological; at that point the name stops being the thing that distinguishes
  // the row and the coach renames it themselves.
  for (let n = 2; n <= 999; n += 1) {
    const candidate = fitWorkoutName(base, ` (copy ${n})`);
    if (free(candidate)) return candidate;
  }
  return first;
}

/** `Push A (copy 4)` → `Push A`. Anything else is returned untouched. */
function stripCopySuffix(name: string): string {
  const m = /^(.*?)\s*\(copy(?:\s+\d+)?\)$/i.exec(name);
  const head = m?.[1];
  if (m === null || head === undefined) return name;
  // ⚠️ A workout literally named `(copy)` strips to the empty string, and an empty name is one
  // the builder's own `validate` refuses. Keep the original in that case: a weird name is better
  // than a name that cannot be saved.
  return head.trim() === "" ? name : head.trim();
}

/**
 * Which section a picked exercise lands in.
 *
 * ⚠️ THE TARGET IS AN INDEX INTO A LIST THAT CHANGES UNDER IT. A `SectionDraft` carries no id, so
 * a remembered index is the only handle there is — and removing section 1 makes the old index 2
 * point at a different section entirely. There is no repair for that case; what there IS, is a
 * refusal to guess. An index that is no longer valid resolves to `null`, the screen asks the
 * coach to choose again, and nothing lands anywhere they did not name.
 *
 * 🔴 AND `null` IN ONLY BECOMES A SECTION WHEN THERE IS EXACTLY ONE, WHICH IS THE HALF THAT
 * RENDERING CAUGHT. The first cut defaulted to the LAST section — sensible on a new workout,
 * which starts with one — and on his real `Coverage Test` it opened reading *"Adding to Section
 * 11 · Recovery"*. An existing workout has no section you are "building"; a coach opening one to
 * add a row would have had it land in the cool-down, with the only warning a caption in a narrow
 * column. So an ambiguous default is REFUSED: with one section there is nowhere else it could go,
 * and with more than one the coach says where.
 */
/* ── ADDING AN EXERCISE — ONE WRITE PATH ───────────────────────────────────────────────────
 *
 * 🔴 §6 RULE (b): "drag never writes what the arrows cannot — ONE write path." This is it. The
 * pointer drag, the click on a picker row and the keyboard's Enter all call this function; none
 * of them builds a section by hand. A second insert written inside a drop handler is how the two
 * paths drift, and the drag one is the one nobody tests.
 *
 * `position` is where the row LANDS, clamped into the section rather than trusted: a drop slot
 * index arrives from the DOM, and a stale one must not throw away the row or append it somewhere
 * a coach did not point at. `position >= length` means the end, which is what a drop past the
 * last card and the click path both mean.
 *
 * ⚠️ IT CLEARS `supersetWithNext` ON THE ROW THAT WAS LAST. A pair is a link between NEIGHBOURS
 * (`supersetRuns`), so inserting between two joined rows would otherwise leave the first pointing
 * at a row that is no longer beside it — the boundary-flag trap the codex records for moves,
 * which bites identically on an insert. */
export function insertExerciseAt(
  sections: readonly SectionDraft[],
  sectionIndex: number,
  position: number,
  row: ExerciseDraft,
): SectionDraft[] {
  if (sectionIndex < 0 || sectionIndex >= sections.length) return [...sections];
  return sections.map((sec, i) => {
    if (i !== sectionIndex) return sec;
    const at = Math.max(0, Math.min(Math.trunc(position), sec.exercises.length));
    const before = sec.exercises.slice(0, at);
    const after = sec.exercises.slice(at);
    // The row the new one now sits in front of can no longer be joined to what follows IT.
    const cleared = before.length > 0
      ? [...before.slice(0, -1), { ...before[before.length - 1]!, supersetWithNext: false }]
      : before;
    const inserted = [...cleared, { ...row, supersetWithNext: false }, ...after];
    // A trailing row can never be joined to a row that does not exist.
    return { ...sec, exercises: inserted.map((x, j, arr) =>
      (j === arr.length - 1 ? { ...x, supersetWithNext: false } : x)) };
  });
}

export function addTargetIndex(sectionCount: number, requested: number | null): number | null {
  if (sectionCount <= 0) return null;
  const valid = requested !== null && Number.isInteger(requested)
    && requested >= 0 && requested < sectionCount;
  if (valid) return requested;
  // Unambiguous, or nothing. Never a guess.
  return sectionCount === 1 ? 0 : null;
}

/**
 * The destination, in words, always carrying its ORDINAL.
 *
 * `null` means nothing is targeted — which is two different situations the SCREEN tells apart
 * (no sections at all, versus sections none of which has been chosen), so this returns one
 * neutral phrase and lets the screen say the actionable half.
 *
 * ⚠️ THE ORDINAL IS NOT DECORATION WHERE IT IS NEEDED. Nothing stops a coach naming two parts `Main` — the field is free
 * text — so `Adding to: Main` is ambiguous exactly when a workout is big enough for the destination to matter.
 * 🔴 W104 (his word 2026-09-30, reversing W88's "part"): the word is SECTION, and an unnamed section has NO generated
 * name — so the ordinal lives only INSIDE this sentence, never as a name: `Adding to: Warm-up` (named), `Adding to: the
 * 2nd section` (unnamed), `Main · 2nd` (a name another section shares). Unique by construction; the common case says
 * only what the coach wrote.
 */
export function describeAddTarget(
  sections: readonly { readonly name: string | null }[],
  index: number | null,
): string {
  if (index === null) return "No section chosen";
  const section = sections[index];
  if (section === undefined) return "No section chosen";
  const name = sectionName(section.name);
  if (name === null) return `the ${ordinalWord(index + 1)} section`;
  const shared = sections.some((s, i) => i !== index && (sectionName(s.name) ?? "").toLowerCase() === name.toLowerCase());
  return shared ? `${name} · ${ordinalWord(index + 1)}` : name;
}

/** `1st`, `2nd`, `3rd`, `4th` … `11th`, `12th`, `13th` … `21st` — the ordinal inside `describeAddTarget`'s sentence. */
export function ordinalWord(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? "th" : n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th";
  return `${String(n)}${suffix}`;
}

/** How a sentence the builder SPEAKS refers to one section when it has only that section: its name, else `the 2nd
 *  section`. Never a name — an ordinal inside a sentence (W104). */
export function sectionReference(name: string | null, index: number): string {
  return sectionName(name) ?? `the ${ordinalWord(index + 1)} section`;
}

/** A reference opening a line (`The 2nd section: …`) — its first letter raised, the rest as written. */
export function sentenceStart(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** 🔴 W88 — a part's count in words (`2 exercises`, `1 exercise`): a bare `2` at the bar's end read as nothing in particular. */
export function exerciseCountWords(n: number): string {
  return plural(n, "exercise");
}

/**
 * 🔴 W88 — THE ARRANGE OUTLINE'S BUTTON EXISTS ONLY WHEN THERE IS SOMETHING TO ARRANGE: two or more items, exercises and
 * parts counted together (the decision recorded with his yes, 2026-09-28). An implicit part is not one the coach sees,
 * so it does not count; its exercises do.
 */
export function arrangeItemCount(sections: readonly { readonly isImplicit: boolean; readonly exercises: readonly unknown[] }[]): number {
  return sections.reduce((n, s) => n + s.exercises.length + (s.isImplicit ? 0 : 1), 0);
}

/* ── W23(e) — `Move to <section>`, ONE MENU ITEM PER SECTION ─────────────────────────────────
 *
 * `docs/EVERFIT-MAP.md` §18-ter depth 3, read live: their exercise card's `⋯` is fourteen items
 * and HALF OF IT IS COMPUTED — `Move to Shoulder Prep` … `Move to Recovery`, one per section in
 * the workout, nine of them on a nine-section workout, each with a `→`.
 *
 * 🔴 **THIS IS THE KEYBOARD-REACHABLE ROUTE TO A GESTURE BOTH PRODUCTS OTHERWISE OFFER ONLY TO A
 * POINTER**, which is the whole argument for copying it. §12 measured their builder as having
 * ZERO focusable elements on its primary work surface, and §18-ter depth 4 found no drag handle
 * on their exercise card at all; ours has drag slots and `MoveControls`, but `MoveControls` moves
 * a row WITHIN its section and nothing but a drag has ever moved one BETWEEN sections.
 *
 * ⚠️ **THE SECTION YOU ARE IN IS NOT OFFERED.** Theirs is "one per section"; a `Move to` naming
 * the section the row is already in is a control that does nothing, which is exactly the defect
 * W22(g) recorded on this product (a `+` that was never passed a caller). If a workout has one
 * section the list is EMPTY and the screen says so rather than drawing a menu of nothing.
 *
 * ⚠️ **THE LABEL IS `describeAddTarget`'s, NOT A SECOND SPELLING.** `Main · 3rd` — with the
 * ordinal, because nothing stops a coach naming two sections `Main` and the builder's own
 * placeholder suggests that very word. Two vocabularies for "which section" inside one screen is
 * this repo's defining bug class with a menu item in it.
 */
export function moveTargets(
  sections: readonly { readonly name: string | null }[],
  fromSection: number,
): { index: number; label: string }[] {
  return sections
    .map((_, index) => ({ index, label: describeAddTarget(sections, index) }))
    .filter((t) => t.index !== fromSection);
}

/* ── W28(e) — THE KEYBOARD'S ROUTE OUT OF A SECTION, WHICH IS THE HALF `moveTargets` CANNOT REACH
 *
 * §6.5 gave a coach the LOOSE ROW — his own word, *"why not have that freedom?"* — and
 * `dropLooseAt` is the mover for it. **Every caller of that mover is a drag handler.** MEASURED
 * 2026-09-21: the two that loosen an EXISTING row live on `SectionDropLine`, which is
 * `aria-hidden` by design, and its own comment sends a keyboard user to *"the exercise card's
 * `⋯`, which calls the same mover"* — a sentence that is TRUE of the cross-section moves beside
 * it and FALSE of this gesture, because the menu has no item for it. So the one feature this
 * builder has that theirs has not got is the one feature only a mouse can reach.
 *
 * 🔴 **THIS IS THE ARGUMENT W32(8) ALREADY MADE ON THIS MENU** — dragging is not essential
 * (WCAG 2.5.7) and a touch coach gets no HTML5 drag at all, which is why the arrows moved into
 * the `⋯` rather than being deleted. The same reasoning reaches the same menu one gesture later.
 * §18-ter depth 3 measured that THEIRS has no such item either: this is a GAP in our own model's
 * reach, not a departure from their screen, which is why it survives §7.2's copy-phase bound.
 *
 * ⚠️ **IT RETURNS THE BOUNDARY, NOT A BOOLEAN, SO THE MENU AND THE HANDLER CANNOT DISAGREE.**
 * Whether to draw the item and where the row lands are the same question asked twice; a `can…`
 * predicate beside a `si + 1` at the call site would be two definitions one line apart — the
 * producer-and-judge shape this repo's codex is named after, and the literal `3` the
 * `minSamePatternGapDays` entry was paid for.
 *
 * ⚠️ **THE BOUNDARY IS THE ONE DIRECTLY BELOW THE ROW'S OWN SECTION.** "Out of the section" is
 * not "to the top of the workout": a coach loosening a row asked to leave a container, not to be
 * relocated, and the nearest boundary is the only one that moves nothing else. If an implicit
 * section already sits there, `tidyImplicit` merges the two — so the row joins the loose run
 * below rather than opening a second invisible one.
 *
 * ⚠️ **A ROW THAT IS ALREADY LOOSE IS NOT OFFERED IT**, and it is OMITTED rather than greyed.
 * `moveTargets` filters out the section you are in for exactly this reason: a destination that is
 * where you already are is not a destination. (W28(a)'s greyed-with-a-reason rule is the other
 * precedent in this menu and it does not apply — it exists to teach a rule the coach cannot see,
 * and this one is drawn on the screen: a loose row is the one with no section bar above it.)
 */
export function looseTarget(
  sections: readonly { readonly isImplicit: boolean }[],
  fromSection: number,
): number | null {
  const section = sections[fromSection];
  if (section === undefined || section.isImplicit) return null;
  return fromSection + 1;
}

/**
 * Where a card's RUN starts and how long it is — what `moveExerciseTo` needs to move a pair whole.
 *
 * 🔴 **A LINKED PAIR TRAVELS AS ONE OBJECT (§6.4), SO THE MENU MOVES THE RUN AND NOT THE CARD.**
 * Moving the upper half of a superset to another section and leaving the lower half behind would
 * silently make two new pairings: the row above the departed one would touch what followed it,
 * and the row it landed behind would claim it. `moveExerciseTo` already re-seals both sections,
 * but only if it is TOLD the span — and the span it is not told is the bug.
 *
 * ⚠️ **IT ASKS `supersetRuns`, WHICH IS WHAT THE CARDS THEMSELVES ARE DRAWN FROM.** A second
 * notion of what a pair is would disagree with the one the coach can see, and this file's own
 * header refuses a third grammar.
 */
export function runAt(
  exercises: ExerciseDraft[],
  index: number,
): { start: number; count: number } {
  for (const run of supersetRuns(exercises)) {
    if (run.includes(index)) return { start: run[0] ?? index, count: run.length };
  }
  return { start: index, count: 1 };
}

/** W95 — `Duplicate` in an exercise's ⋯ (Everfit's menu has it; his 2026-09-29 stills). The copy is the whole row — sets,
 *  tempo, each side, note — and it lands AFTER the row's superset run (`runAt`), unlinked: inserted between a pair it
 *  would take the pair's link and silently re-pair the superset. Pure; the section passed in is never mutated. */
export function duplicateExercise(section: SectionDraft, index: number): SectionDraft {
  const row = section.exercises[index];
  if (row === undefined) return section;
  const run = runAt(section.exercises, index);
  const at = run.start + run.count;
  const copy: ExerciseDraft = { ...row, supersetWithNext: false, sets: row.sets.map((s) => ({ ...s })) };
  return { ...section, exercises: [...section.exercises.slice(0, at), copy, ...section.exercises.slice(at)] };
}

/* `filterLibrary` (word-AND over the NAME) was RETIRED in W72 step 5: the builder's pane and the Library page now ask
 * ONE search, `exerciseShelf.shelfMatches` — the same word-AND, over the name WITH its generated long forms, the
 * muscles and the equipment. Its cases moved to `exerciseShelf.test.ts` unchanged. */

/**
 * How many library rows are shown before the coach is asked to keep typing.
 *
 * His library is 108 rows today and the catalogue behind it is 465, so an uncapped list is a
 * scroll rather than a choice. The cap is stated on screen with the true total beside it — a
 * truncation nobody is told about is the shape this repo has paid for.
 */
export const LIBRARY_PAGE = 40;

/* ── The window's close, and the section shape a library row shows ──────────────────────────
 *
 * WORKSTREAM B, SESSION B-4, session 10. The owner, on Everfit's editors: *"you see they're just
 * windows which make the rest of the background screen darker? very clean. both exercise and
 * workouts editors/creators."*
 *
 * The LOOK is CSS. What needs a rule is the half a dimmed backdrop introduces and a full page
 * never had: **a window can be dismissed by a gesture that was not a decision.** Escape is one
 * keystroke, and the builder holds a whole workout of unsaved edits.
 */

/**
 * What a close ATTEMPT does — Escape, or the window's own Close.
 *
 * 🔴 A DIRTY DRAFT IS NEVER DISCARDED BY A GESTURE. `<dialog>`'s `cancel` event is cancelable
 * precisely so this decision can be taken, and taking it is the whole reason the builder may
 * become a window at all: as a full PAGE the browser's own "leave site?" prompt was never in
 * play either, but a page does not vanish under one keystroke.
 *
 * ⚠️ AND ESCAPE WHILE THE QUESTION IS ON SCREEN DISMISSES THE QUESTION — it does not answer it.
 * The alternative reading, "press Escape twice to discard", is defensible and is the wrong
 * direction here: Escape's universal meaning is *back out of the thing in front of me*, and the
 * thing in front is the question. Discarding stays an explicit, labelled act, so the worst case
 * of this rule is a coach clicking one more button and the worst case of the other is lost work.
 * The same asymmetry `EngineRestCeiling` ships under.
 *
 * ⚠️ A SAVE IN FLIGHT OUTRANKS BOTH. `executeSave` is a SEQUENCE of PostgREST requests with no
 * transaction across them (`b4-5` ruler 3 pins why), so closing midway unmounts the only thing
 * that would have reported a failure — and the writes carry on regardless. The window stays and
 * says so.
 */
export type CloseAttempt =
  /** Nothing is at stake: go. */
  | "close"
  /** Unsaved work: ask, and stay. */
  | "confirm"
  /** The question is already on screen: take it away, keep editing. */
  | "dismissConfirm"
  /** A save is in flight: the window may not leave until it reports. */
  | "wait";

export function closeAttempt(state: {
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly confirming: boolean;
  /** W85 — the WORKOUT BUILDER's stake. Absent for every other editor, which keeps the `dirty` rule unchanged. */
  readonly stake?: CloseStake;
}): CloseAttempt {
  if (state.saving) return "wait";
  if (state.confirming) return "dismissConfirm";
  // 🔴 HIS RULE (W85): ask ONLY when at least one exercise is still in the workout AND it differs from how it opened.
  if (state.stake !== undefined) return state.stake.exercises > 0 && state.stake.changedSinceOpen ? "confirm" : "close";
  return state.dirty ? "confirm" : "close";
}

/**
 * 🔴 W85 — WHAT CLOSING THE WORKOUT BUILDER PUTS AT STAKE. His words, 2026-09-27: *"don't we need an X top left or right of
 * the workout builder?? now only way to close it is the done but what if i don't want to save a workout or the changes i
 * did?? ok with a warning, only if i added at least 1 exercise and it is still there. if the title is empty and no
 * exercises then no warning needed."*
 *
 * ⚠️ THE BUILDER SAVES AS YOU TYPE (W72 step 3), SO "DON'T SAVE" IS A RESTORE, NEVER A WITHHELD WRITE. By the time the ✕ is
 * pressed the pause has usually written the edits already; `dirty` says only whether the LAST keystrokes are still
 * unwritten, which is why his question is asked of the draft AT OPEN (`changedSinceOpen`), not of `dirty`. Fable's design
 * call, 2026-09-27: `Discard changes` writes the draft at open back through the normal save path.
 */
export type CloseStake = {
  /** Rows in the workout NOW. */
  readonly exercises: number;
  /** The draft differs from the one the window opened on (`sameDraft`). */
  readonly changedSinceOpen: boolean;
  /** The window was opened on `#/workouts/new` — any row it holds was created here. */
  readonly createdHere: boolean;
};

/** Rows in a draft — the count his rule reads. */
export function exerciseCount(d: WorkoutDraft): number {
  return d.sections.reduce((n, s) => n + s.exercises.length, 0);
}

/** Whether two drafts are the same WORKOUT. The id is not the workout: a new one gains its id at the first save. */
export function sameDraft(a: WorkoutDraft, b: WorkoutDraft): boolean {
  return JSON.stringify({ ...a, id: null }) === JSON.stringify({ ...b, id: null });
}

/**
 * What a `close` answer DOES in the builder (the three silent exits of his rule).
 *  · no exercise left — nothing here can be saved (`validate` refuses a workout with no exercise), so nothing is written;
 *    and a row this window CREATED is removed (W17d's zero-copies delete, measured at click by `removeWorkout`) so no
 *    empty template lingers (a TITLED workout created here and emptied is removed too — Fable's ruling, 2026-09-27, F5).
 *    His case (a) is the empty title with no exercise; a titled one with no exercise is the same
 *    fact — the row can only hold a version the coach has since emptied.
 *  · otherwise — leave. 🔴 W95 (his word, 2026-09-29): the workout saves ONLY ON SAVE, so nothing is written behind the
 *    coach's back and a close that asks nothing writes nothing (an undo back to the start is the workout as saved).
 */
export type CloseDo = "leave" | "removeThenLeave";
export function closeOutcome(stake: CloseStake): CloseDo {
  if (stake.exercises === 0) return stake.createdHere ? "removeThenLeave" : "leave";
  return "leave";
}

/** What `Discard changes` does: a workout created here goes (it did not exist when the window opened); any other is left
 *  exactly as it was saved — W95: nothing was written, so nothing is written to put it back. */
export function discardOutcome(stake: CloseStake): "remove" | "restore" {
  return stake.createdHere ? "remove" : "restore";
}

/** One section of a workout, as a library row summarises it. */
export type SectionCount = {
  readonly name: string | null;
  readonly exercises: number;
  /* W23(a) — the ids in THIS section, so a chip can list what is inside it. Ids and not names:
     the workouts query reads `coach_workout_exercises(id, exercise_id)` and the library is a
     separate flat read, which is the one-flat-read shape F11 lesson 27 left behind. Resolving
     here would mean either a nested embed or a second vocabulary for the same map. */
  readonly exerciseIds: readonly string[];
};

/**
 * How many sections a library row names before it stops.
 *
 * ⚠️ AND WHY TRUNCATION ONLY BEGINS AT `CAP + 2`. Hiding ONE item behind "and 1 more" costs a
 * reader the same line it was meant to save, so a cap that fires there is not paying for itself.
 * Measured on his real library: 6 of 9 templates have 3 sections, two have 5, and `Coverage Test`
 * has 11 — so with this rule every template but one is shown WHOLE and the cap exists for the
 * case that actually needs it.
 */
export const SHAPE_CAP = 4;

/**
 * 🔴 W104 (his word 2026-09-30: *"not have a fix name like section or block 1,2,3 etc."*) — a section's name is the
 * COACH's, or NOTHING. This replaced `sectionDisplayName`, which generated `Section N` (then W88's `Part N`) for the 27%
 * of template sections a coach never named. Absence is spelled as absence (`null`): each surface says what it draws for
 * it — the builder a hint, Arrange the bare word `Section`, the list caption nothing, a sentence `describeAddTarget` /
 * `sectionReference`'s ordinal, and a surface showing what the CLIENT sees the phone's own rule (`phoneSectionName`).
 */
export function sectionName(name: string | null): string | null {
  const trimmed = (name ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * A workout's shape, in the coach's own section names — the owner's ask, from Everfit's list:
 * `SHOULDER PREP (9) / BLOCK A — SKILL (2)` where ours said `20 exercises · 11 sections`.
 *
 * ⚠️ AN UNNAMED SECTION IS NOT NAMED AT ALL (W104, his word 2026-09-30) — MEASURED, **10 of 37
 * template sections (27%) carry no name**, so the blank is the DEFAULT; the line names only what
 * the coach named, and a workout whose every section is unnamed reads as its exercise count.
 *
 * ⚠️ THE TOTAL IS STATED ONLY WHEN THE LIST IS CUT. Printing it alongside a complete list is a
 * second statement of the same fact — `WEB-APP-PLAN.md` §2b's *"extra bs which is too much
 * info"*, which the builder's own collapsed summary was already trimmed for once. When the list
 * is cut the total is no longer derivable, so it is said.
 */
export function describeShape(shape: readonly SectionCount[]): string {
  if (shape.length === 0) return "No sections yet";
  // W104 — only NAMED sections are named; an unnamed one contributes its exercises to the count and no name.
  const named = shape.flatMap((s) => { const n = sectionName(s.name); return n === null ? [] : [`${n} (${s.exercises})`]; });
  if (named.length === 0) { const all = shape.reduce((n, s) => n + s.exercises, 0); return plural(all, "exercise"); }
  // Truncating to hide a single row buys nothing, so the cap only bites past CAP + 1.
  if (named.length <= SHAPE_CAP + 1) return named.join(" · ");
  const total = shape.reduce((n, s) => n + s.exercises, 0);
  const rest = named.length - SHAPE_CAP;
  const noun = total === 1 ? "exercise" : "exercises";
  return `${named.slice(0, SHAPE_CAP).join(" · ")} · and ${rest} more (${total} ${noun})`;
}

/* `shapeChips`, `appLibraryChips` and `chipDisclosure` — the W23(a) / W21 (C) section CHIPS on the workout library's
 * row — were RETIRED in W72 step 5 by his word on Proposal 5 (*"yes just without these strange chips above or below
 * each exercise row"*): the row states its sections as caption TEXT (`workoutShelf.sectionCaption`). */

/**
 * W21 (C) — DOES THIS WORKOUT MATCH WHAT THE COACH TYPED.
 *
 * ⚠️ **AN EMPTY OR BLANK QUERY MATCHES EVERYTHING**, so the field starts as a no-op rather than
 * as a filter hiding the library. A query of spaces is the same thing typed by accident.
 *
 * ⚠️ **CASE-FOLDED, AND THE NAME ONLY.** Matching descriptions too would return rows whose match
 * a coach cannot see on the card, which is the shape that teaches people a search is broken —
 * and the placeholder says `Search workouts`, so the name is what it promises.
 *
 * ⚠️ **SUBSTRING, NOT PREFIX.** A coach looking for `Upper A — pulling` types `pull`.
 */
export function matchesWorkoutQuery(name: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  return name.toLowerCase().includes(q);
}

/**
 * What a workout needs, derived from the exercises it holds.
 *
 * The owner's second ask from the same screenshots. It is DERIVED and never stored: a coach who
 * had to keep an equipment list in step with the exercises they added would keep it wrong, and a
 * stored copy of a derivable fact is this repo's defining bug class with a chip row in it.
 *
 * 🔴 THE AMBIENT SET IS THE RULE, NOT A `!== "none"` FILTER. `none` is a real member of
 * `coach_exercises.equipment` — live on 36 of 108 rows — so an unfiltered summary puts a chip
 * reading `No Equipment` on nearly every workout. The first cut filtered exactly that token; the
 * enum states the honest version in its own words, *"an exercise requiring only these needs no
 * equipment"*, and it also covers `wall` (5 rows) and `doorway` (1). Both are live in his library
 * today, so the narrow filter was already wrong rather than merely fragile. `EQUIPMENT_AMBIENT`
 * is GENERATED from that set.
 *
 * ⚠️ AN EXERCISE WHOSE LIBRARY ROW IS NOT LOADED IS REPORTED, NEVER SKIPPED. Silently omitting it
 * makes an incomplete answer look like a complete one — a coach reads "needs a pull-up bar" and
 * takes it as the whole list. `unknown` is what lets the screen say the summary is partial.
 *
 * ⚠️ A NULL `equipment` COLUMN IS UNKNOWN AND NOT "NEEDS NOTHING". Measured: zero of the 108 live
 * rows are null, so this branch is unexercised today — and the two answers are different claims,
 * so collapsing them would make a missing value read as a measurement.
 */
export type EquipmentSummary = {
  /** Canonical raw values, deduplicated and ordered — labels are the screen's business. */
  readonly items: readonly string[];
  /** Exercises whose equipment could not be read at all. */
  readonly unknown: number;
  /**
   * How many exercises the answer is ABOUT.
   *
   * 🔴 WITHOUT THIS, AN EMPTY WORKOUT MAKES A CONFIDENT CLAIM ABOUT EXERCISES THAT DO NOT EXIST
   * — found by rendering a new workout, which read *"Nothing — every exercise here is
   * bodyweight."* over a workout with no exercises at all. Vacuously true and it reads as a
   * measurement, which is this repo's defining failure shape in one sentence. `items: []` cannot
   * tell the two apart on its own, so the count is returned and the screen is made to say which
   * it is.
   */
  readonly considered: number;
};

export function workoutEquipment(
  exercises: readonly { readonly libraryExerciseId: string }[],
  byId: ReadonlyMap<string, { readonly equipment: readonly string[] | null }>,
  ambient: readonly string[],
): EquipmentSummary {
  const ambientSet = new Set(ambient);
  const items = new Set<string>();
  let unknown = 0;
  const considered = exercises.length;
  for (const ex of exercises) {
    const lib = byId.get(ex.libraryExerciseId);
    if (lib === undefined || lib.equipment === null) { unknown += 1; continue; }
    for (const raw of lib.equipment) {
      const token = raw.trim();
      if (token === "" || ambientSet.has(token)) continue;
      items.add(token);
    }
  }
  // Sorted so the row is stable between renders and between coaches. The tokens are snake_case
  // spellings of their own labels, so this is the reading order too.
  return { items: [...items].sort(), unknown, considered };
}

/* ── R18 · a row's picture, and the THREE answers there are ──────────────────────────────────── */

/**
 * What the builder knows about one drafted row's media.
 *
 * 🔴 `null` WAS TWO ANSWERS AND THE SCREEN DREW THE WRONG ONE. `mediaFor` returned `null` both for
 * a drafted row whose library entry has been DELETED — where "no video" is the truth — and while
 * the library fetch was still IN FLIGHT, where it is a claim about a row whose media simply has
 * not arrived. The old plate blurred the difference; an explicit no-video glyph cannot, so on
 * every builder open the coach saw a workout of rows asserting they had no video, which then
 * quietly filled in.
 *
 * ⚠️ AN EMPTY LIBRARY ARRAY CANNOT CARRY THIS. `[]` means "loaded, and the coach has nothing" AND
 * "not loaded yet" — the same collision one level up, which is why `library` is now `| null` and
 * the loading state is a value rather than a length.
 */
export type RowMedia =
  | { state: "pending" }
  | { state: "missing" }
  | { state: "found"; demoVideoUrl: string | null; thumbnailUrl: string | null };

/** The one place the three cases are decided. `null` library = the fetch has not resolved. */
export function rowMedia(
  library: readonly { id: string; demoVideoUrl: string | null; thumbnailUrl: string | null }[] | null,
  libraryExerciseId: string,
): RowMedia {
  if (library === null) return { state: "pending" };
  const e = library.find((x) => x.id === libraryExerciseId);
  if (e === undefined) return { state: "missing" };
  return { state: "found", demoVideoUrl: e.demoVideoUrl, thumbnailUrl: e.thumbnailUrl };
}
